const BASE_URL = "/api/v1";

export class ApiError extends Error {
  status: number;
  details?: string[];
  body?: unknown;

  constructor(status: number, error: string, details?: string[], body?: unknown) {
    super(error);
    this.name = "ApiError";
    this.status = status;
    this.details = details;
    this.body = body;
  }
}

export function getErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.details?.length) return `${err.message}: ${err.details.join(", ")}`;
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return "An unexpected error occurred";
}

export interface Paginated<T> {
  total: number;
  items: T[];
}

export interface CursorPaginated<T> extends Paginated<T> {
  next_cursor: string | null;
}

async function parseError(response: Response): Promise<ApiError> {
  let error = `API error: ${response.status}`;
  let details: string[] | undefined;
  let body: unknown;
  try {
    body = await response.json();
    const b = body as Record<string, unknown> | undefined;
    if (typeof b?.error === "string") error = b.error;
    else if (b?.error && typeof (b.error as Record<string, unknown>).message === "string")
      error = (b.error as Record<string, unknown>).message as string;
    if (Array.isArray(b?.details)) details = b.details as string[];
  } catch {
    // response wasn't JSON, use default message
  }
  return new ApiError(response.status, error, details, body);
}

/** Where a request without a valid login (a 401, the realtime stream's `unauthorized`) sends the user. */
export function redirectToLogin(): void {
  window.location.href = "/login";
}

async function send(path: string, init: RequestInit): Promise<Response> {
  const response = await fetch(`${BASE_URL}${path}`, { credentials: "include", ...init });
  if (!response.ok) {
    if (response.status === 401 && !path.startsWith("/auth/")) {
      redirectToLogin();
    }
    throw await parseError(response);
  }
  return response;
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await send(path, {
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  if (response.status === 204) return undefined as T;
  return response.json();
}

// No Content-Type: the browser sets the multipart boundary itself.
async function upload<T>(path: string, formData: FormData, method = "POST"): Promise<T> {
  const response = await send(path, { method, body: formData });
  return response.json();
}

async function downloadCsv(path: string, filename: string): Promise<void> {
  const response = await send(path, {});
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

type QueryValue = string | number | boolean | null | undefined | readonly string[];
type Query = Record<string, QueryValue>;

// "" or "?…". Skips undefined, null and ""; false and 0 are sent; arrays repeat the key.
function buildQuery(params: Query): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) for (const v of value) qs.append(key, v);
    else if (value != null && value !== "") qs.append(key, String(value));
  }
  const s = qs.toString();
  return s ? `?${s}` : "";
}

const withBody = (method: string, body: unknown): RequestInit =>
  body === undefined ? { method } : { method, body: JSON.stringify(body) };

const get = <T>(path: string, query?: Query) =>
  request<T>(query ? `${path}${buildQuery(query)}` : path);
const post = <T>(path: string, body?: unknown) => request<T>(path, withBody("POST", body));
const put = <T>(path: string, body?: unknown) => request<T>(path, withBody("PUT", body));
const patch = <T>(path: string, body?: unknown) => request<T>(path, withBody("PATCH", body));
const del = <T = void>(path: string) => request<T>(path, { method: "DELETE" });

export interface SignalFilters {
  tenant_id?: string | null;
  workflow_id?: string;
  data_type?: string;
  master_company_id?: string;
  matched?: boolean;
  // The matcher's match strength (`rating`), at least.
  min_rating?: number;
  // Any of these verdicts on the latest brief.
  research_verdict?: SignalBriefVerdict[];
  // The research agent's confidence in its latest verdict, at least. Leaves
  // out signals that have no brief.
  min_research_confidence?: number;
  // Only signals with (true) / without (false) a workflow step waiting for a person to continue.
  awaiting_approval?: boolean;
  // Only signals a workflow step put on this prospect list.
  collection_id?: string;
  // Only signals evaluated at or after this moment (ISO).
  since?: string;
  // The signal's country, any of (ISO alpha-2; SIGNAL_NO_COUNTRY: none known).
  country?: string[];
}

/** The `country` filter value for signals with no country known. */
export const SIGNAL_NO_COUNTRY = "none";

function signalQuery(params: SignalFilters): Query {
  return {
    tenant_id: params.tenant_id,
    workflow_id: params.workflow_id,
    data_type: params.data_type,
    master_company_id: params.master_company_id,
    matched: params.matched,
    min_rating: params.min_rating,
    research_verdict: params.research_verdict,
    min_research_confidence: params.min_research_confidence,
    awaiting_approval: params.awaiting_approval,
    collection_id: params.collection_id,
    since: params.since,
    country: params.country,
  };
}

/** One string per distinct filter set, for query keys. */
export function signalFilterKey(params: SignalFilters): string {
  return JSON.stringify(signalQuery(params));
}

function companyQuery(params: CompanyListParams): Query {
  return {
    country: params.country,
    name: params.name,
    industry_keywords: params.industry_keywords,
    served_vertical_keywords: params.served_vertical_keywords,
    org_form: params.org_form,
    employee_size_bucket: params.employee_size_bucket,
    job_ad_keywords: params.job_ad_keywords,
    technology: params.technology,
    contact_role_type: params.contact_role_type,
    contact_name: params.contact_name,
    revenue_min: params.revenue_min,
    revenue_max: params.revenue_max,
    operating_result_min: params.operating_result_min,
    operating_result_max: params.operating_result_max,
    currency: params.currency,
    regulatory_filing_status: params.regulatory_filing_status,
    regulatory_filing_keyword: params.regulatory_filing_keyword,
    regulatory_filing_source: params.regulatory_filing_source,
    sort_by: params.sort_by,
    sort_dir: params.sort_dir,
  };
}

// Per-organization credentials for the agent runtime. The secret itself is
// never returned by the API — only whether one is stored and its last 4 chars.
export interface AgentCredential {
  slug: string;
  name: string;
  description: string;
  env_var: string;
  stored: boolean;
  env_fallback_available: boolean;
  hint: string;
  updated_at: string | null;
  updated_by_email: string | null;
  encryption_configured: boolean;
}

// Org-level agent knobs that are not themselves secrets.
export interface AgentSettings {
  // Dollar ceiling for one run; null means no cap.
  max_budget_usd: number | null;
}

export interface AgentCredentialTestResult {
  ok: boolean;
  detail: string;
  source: string;
}

// Paid contact enrichment (Clay, GetLeads). Buying is billed to the organization
// in the URL; every data point delivered lands in a ledger with its price.
// company_profile is a company lookup an agent buys, never a per-person reveal.
export type ContactDataPoint = "person_profile" | "work_email" | "mobile_phone" | "company_profile";

export interface ContactPurchaseBody {
  collection_id: string;
  /** One of these: the company, or the unresolved list entry it is not yet. */
  master_company_id?: string | null;
  collection_entry_id?: string | null;
  limit: number;
  // Per-person data points to buy on top of the profile.
  data_points: ContactDataPoint[];
  // Titles to look for; empty or null means anyone at the company.
  titles?: string[] | null;
  // Omitted: the first provider enabled.
  provider?: string;
}

/** A provider the organization can buy from now, with its unit prices. */
export interface ContactPurchaseProvider {
  provider: string;
  provider_name: string;
  /** False for a partner whose data is free (GetLeads): no prices, no spend limits. */
  billed: boolean;
  currency: string;
  available_data_points: ContactDataPoint[];
  prices: Partial<Record<ContactDataPoint, number>>;
}

export interface ContactBudgetInfo {
  limit: number;
  spent: number;
  reserved: number;
  remaining: number;
}

export interface ContactPurchaseEstimate {
  provider: string;
  provider_name: string;
  billed: boolean;
  currency: string;
  prices: Partial<Record<ContactDataPoint, number>>;
  max_cost: number;
  max_limit: number;
  available_data_points: ContactDataPoint[];
  budget: {
    organization: ContactBudgetInfo | null;
    tenant: ContactBudgetInfo | null;
    remaining: number;
  };
  persona: { titles: string[] };
  contacts_on_list: number;
  company_name: string;
  company_domain: string | null;
  can_purchase: boolean;
  blocked_reason: string | null;
}

export type ContactPurchaseStatus = "pending" | "running" | "completed" | "partial" | "failed";

export interface ContactPurchase {
  id: string;
  status: ContactPurchaseStatus;
  provider: string;
  provider_name: string;
  /** False for a partner provider whose data is free (GetLeads). */
  billed: boolean;
  // "company_lookup": one company looked up by an agent, no people.
  kind: "people" | "company_lookup";
  collection_id: string | null;
  master_company_id: string | null;
  /** A purchase at an unresolved list entry (no master company). */
  collection_entry_id: string | null;
  company_name: string | null;
  limit: number;
  data_points: ContactDataPoint[];
  titles: string[];
  currency: string;
  reserved_amount: number;
  charged_amount: number;
  found_count: number;
  error: string | null;
  user_email: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface ContactSpendLimitRow {
  tenant_id: string | null;
  tenant_name: string | null;
  // null: no limit (for the organization row that means no buying at all).
  monthly_limit: number | null;
  spent: number;
  reserved: number;
  remaining: number | null;
}

export interface ContactSpendLimits {
  currency: string;
  month: string;
  organization: ContactSpendLimitRow;
  tenants: ContactSpendLimitRow[];
}

export type ContactUsageGroupBy = "tenant" | "user" | "data_point" | "provider";

export interface ContactUsage {
  month: string;
  group_by: ContactUsageGroupBy;
  currency: string;
  total: number;
  rows: { key: string | null; label: string; data_point: ContactDataPoint; quantity: number; amount: number }[];
}

// Platform-level provider setup (developer only). The API key is write-only.
export interface DataProviderConfigField {
  name: string;
  label: string;
  secret: boolean;
  required: boolean;
  help_text: string;
  default: string;
  // Already covered by its default; the setup page collapses these.
  advanced: boolean;
}

export interface DataProvider {
  provider: string;
  name: string;
  /** False for a partner whose data is free to organizations: no credit pack or prices. */
  billed: boolean;
  enabled: boolean;
  secret_stored: boolean;
  secret_hint: string;
  config: Record<string, string>;
  config_fields: DataProviderConfigField[];
  supported_data_points: ContactDataPoint[];
  // What the saved settings can buy, key permitting.
  available_data_points: ContactDataPoint[];
  // How to make each supported but unavailable data point available.
  unavailable_hints: Partial<Record<ContactDataPoint, string>>;
  encryption_configured: boolean;
  updated_at: string | null;
  updated_by_email: string | null;
}

export interface DataProviderTestResult {
  ok: boolean;
  detail: string;
  // `details`: what the provider reports about the account, as label/value rows.
  info: Record<string, unknown> & { details?: { label: string; value: string | number }[] };
}

// A data point's price in force, derived: credits × pack price per credit × markup.
export interface DataProviderPrice {
  data_point: ContactDataPoint;
  // null: the default, paid by every organization without its own markup.
  organization_id: string | null;
  organization_name: string | null;
  credits: number;
  markup: number;
  unit_price: number;
  currency: string;
}

// An append-only credit rate: the pack (default rows) or one organization's markup.
export interface DataProviderCreditRate {
  id: string;
  provider: string;
  organization_id: string | null;
  organization_name: string | null;
  package_price: number | null;
  package_credits: number | null;
  credit_price: number | null;
  markup: number;
  currency: string;
  effective_from: string;
  created_at: string;
  created_by_email: string | null;
  in_force: boolean;
}

// Agent types
// Runs are started from the prospect order they fulfil; the order supplies the
// tenant, the brief and the destination list.
// One transcript entry from a run: what the agent said, called, or got back.
export interface AgentRunEvent {
  seq: number;
  kind:
    | "assistant_text"
    | "thinking"
    | "tool_use"
    | "tool_result"
    | "result"
    | "error";
  tool_name: string | null;
  text: string | null;
  payload: Record<string, unknown> | null;
  is_error: boolean;
  created_at: string;
}

export interface AgentRunTranscript {
  run_id: string;
  status: string;
  // Pass back as `after_seq` to fetch only what is new.
  last_seq: number;
  // False once the run is terminal — stop polling.
  is_active: boolean;
  events: AgentRunEvent[];
}

/** A run still in flight on a list's row (`agents/activity.py`). */
export interface ActiveAgentRun {
  id: string;
  agent_type: string;
  // The agent's name as the run snapshotted it; null on old runs.
  agent_name: string | null;
  status: string;
}

export interface AgentRun {
  id: string;
  tenant_id: string;
  status: string;
  agent_type: string;
  config: Record<string, unknown>;
  summary: string | null;
  iterations_used: number | null;
  total_input_tokens: number | null;
  total_output_tokens: number | null;
  error: string | null;
  started_at: string | null;
  completed_at: string | null;
  collection_id: string | null;
  prospect_order_id: string | null;
  signal_id?: string | null;
  // The agent definition it was started from; `config.agent` is what it used.
  agent_definition_id?: string | null;
  // A structured agent's result.
  output?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}


export interface GoogleSetupInfo {
  redirect_uri: string;
  scopes: string[];
  configured: boolean;
}

/** One user's Google account in an organization; sheet sources point at one. */
export interface MicroAppGoogleConnection {
  id: string;
  organization_id: string;
  google_account_email: string;
  scopes: string;
  /** "active" | "needs_reconnect" */
  status: string;
  last_error: string | null;
  connected_by_user_id: string | null;
  connected_by_name: string | null;
  /** Only the caller's own connections can be chosen for a sheet. */
  is_mine: boolean;
  source_count: number;
  created_at: string;
  updated_at: string;
}

export interface MicroAppSheetSource {
  id: string;
  organization_id: string;
  tenant_id: string | null;
  slug: string;
  title: string;
  spreadsheet_id: string;
  /** The Google connection this sheet is read with; null = never authorized. */
  google_connection_id: string | null;
  allowed_ranges: string[];
  /** True when the source grants every range (allowed_ranges contains "*"). */
  allow_all: boolean;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface SheetTabInfo {
  title: string;
  rows: number;
  columns: number;
  suggested_range: string;
}

export interface SheetInspectResult {
  ok: boolean;
  spreadsheet_id: string;
  title: string;
  tabs: SheetTabInfo[];
  error: string | null;
  error_kind: string | null;
}

export interface SheetRangeResult {
  range: string;
  ok: boolean;
  rows: number;
  columns: string[];
  sample_rows: unknown[][];
  suggested_header_row: number | null;
  suggested_range: string | null;
  error: string | null;
  error_kind: string | null;
}

export interface SheetSourceTestResult {
  ok: boolean;
  spreadsheet_id: string;
  title: string;
  tabs: SheetTabInfo[];
  results: SheetRangeResult[];
  warnings: string[];
  error: string | null;
  error_kind: string | null;
}

export const api = {
  // Auth
  login: (email: string, password: string, totp_code?: string) =>
    post<LoginResponse>("/auth/login", { email, password, totp_code }),
  logout: () => post<void>("/auth/logout"),
  me: () => get<AuthUser>("/auth/me"),
  changePassword: (data: { current_password: string; new_password: string }) =>
    post<void>("/auth/change-password", data),
  // ui_preferences merges key by key on the server; null drops a key.
  updateProfile: (data: { display_name?: string; ui_preferences?: Record<string, unknown> }) =>
    patch<AuthUser>("/auth/me", data),
  acceptInvitation: (token: string, data: { display_name: string; password: string }) =>
    post<AuthUser>(`/auth/invitations/${token}/accept`, data),
  mfaSetup: () => post<MfaSetupResponse>("/auth/mfa/setup"),
  mfaVerify: (code: string) => post<MfaVerifyResponse>("/auth/mfa/verify", { code }),
  mfaDisable: (code: string) => post<void>("/auth/mfa/disable", { code }),
  regenerateBackupCodes: (code: string) =>
    post<MfaVerifyResponse>("/auth/mfa/backup-codes", { code }),
  uploadAvatar: (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return upload<AuthUser>("/auth/me/avatar", formData, "PUT");
  },
  deleteAvatar: () => del("/auth/me/avatar"),
  getSessions: () => get<AccountSession[]>("/auth/sessions"),
  revokeSession: (id: string) => del(`/auth/sessions/${id}`),
  revokeOtherSessions: () => post<{ revoked: number }>("/auth/sessions/revoke-others"),
  getConnectedApps: () => get<ConnectedApp[]>("/auth/connected-apps"),
  revokeConnectedApp: (clientId: string) =>
    del(`/auth/connected-apps/${encodeURIComponent(clientId)}`),
  ssoGoogleMfa: (code: string) =>
    post<{ redirect_to: string; user: AuthUser }>("/auth/google/mfa", { code }),

  // Admin - Users
  getUsers: () => get<AuthUser[]>("/auth/users"),
  getMcpUsage14d: () => get<Record<string, number[]>>("/auth/mcp-usage/users-14d"),
  updateUser: (userId: string, data: { roles?: string[]; is_active?: boolean }) =>
    patch<AuthUser>(`/auth/users/${userId}`, data),

  // Tenants
  getTenants: (params?: { organization_id?: string }) =>
    get<Tenant[]>("/tenants", { organization_id: params?.organization_id }),
  getTenantStats: (params?: { organization_id?: string }) =>
    get<TenantStatsResponse[]>("/tenants/stats", { organization_id: params?.organization_id }),
  createTenant: (data: TenantCreate) => post<TenantCreateResponse>("/tenants", data),
  updateTenant: (id: string, data: TenantUpdate) => patch<Tenant>(`/tenants/${id}`, data),
  deleteTenant: (id: string) => del(`/tenants/${id}`),
  getTenantData: (tenantId: string, objectType: string, limit = 50, offset = 0) =>
    get<TenantDataResponse>(`/tenants/${tenantId}/data`, {
      object_type: objectType,
      limit,
      offset,
    }),
  getTenant: (id: string) => get<Tenant>(`/tenants/${id}`),
  updateTenantIcp: (id: string, data: TenantIcpUpdate) =>
    put<Tenant>(`/tenants/${id}/icp`, data),
  startTenantProfileResearch: (id: string) =>
    post<TenantProfileRun>(`/tenants/${id}/profile-research`),
  startTenantWorkflowDesign: (id: string) =>
    post<TenantProfileRun>(`/tenants/${id}/workflow-design`),
  getLatestTenantProfileResearch: (id: string) =>
    get<TenantProfileRun | null>(`/tenants/${id}/profile-research/latest`),
  updateTenantCurrency: (id: string, currency: string) =>
    put<Tenant>(`/tenants/${id}/currency`, { currency }),
  updateTenantRequiredProperties: (id: string, data: TenantRequiredPropertiesUpdate) =>
    put<Tenant>(`/tenants/${id}/required-properties`, data),
  getPropertyCatalog: () => get<PropertyCatalogResponse>(`/tenants/property-catalog`),
  uploadTenantLogo: (id: string, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return upload<Tenant>(`/tenants/${id}/logo`, formData, "PUT");
  },
  deleteTenantLogo: (id: string) => del(`/tenants/${id}/logo`),

  // Providers
  getProviders: () => get<Provider[]>("/providers"),

  // Connections
  getConnections: (params?: { organization_id?: string }) =>
    get<Connection[]>("/connections", { organization_id: params?.organization_id }),
  createConnection: (data: CreateConnectionInput) => post<Connection>("/connections", data),
  updateConnectionCredentials: (id: string, credentials: Record<string, unknown>) =>
    patch<Connection>(`/connections/${id}`, { credentials }),
  /**
   * Probe what this token may actually do with sequences.
   *
   * Worth running once per portal: whether a portal-wide private-app token can
   * act on behalf of a given user is not settled by HubSpot's docs, and this
   * reports what the portal really allows without mailing anyone.
   */
  testSequenceAccess: (id: string, params?: { sender_email?: string; user_id?: string }) =>
    post<SequenceAccessResult>(
      `/connections/${id}/test-sequences${buildQuery({
        sender_email: params?.sender_email,
        user_id: params?.user_id,
      })}`,
    ),

  // Connection Mappings
  getConnectionMappings: (connectionId: string) =>
    get<ConnectionMapping[]>(`/connections/${connectionId}/mappings`),
  createConnectionMapping: (connectionId: string, data: CreateMappingInput) =>
    post<ConnectionMapping>(`/connections/${connectionId}/mappings`, data),
  deleteConnectionMapping: (connectionId: string, mappingId: string) =>
    del(`/connections/${connectionId}/mappings/${mappingId}`),
  reassignTenants: (connectionId: string) =>
    post<Record<string, number>>(`/connections/${connectionId}/reassign-tenants`),

  // Pipelines
  getConnectionPipelines: (connectionId: string) =>
    get<Pipeline[]>(`/connections/${connectionId}/pipelines`),

  // Login domains → client, and the people (reps) behind a connection's activity
  getOwnerDomains: (connectionId: string) =>
    get<OwnerDomain[]>(`/connections/${connectionId}/owner-domains`),
  setOwnerDomain: (connectionId: string, domain: string, tenantId: string) =>
    put<OwnerDomain>(
      `/connections/${connectionId}/owner-domains/${encodeURIComponent(domain)}`,
      { tenant_id: tenantId },
    ),
  deleteOwnerDomain: (connectionId: string, domain: string) =>
    del(`/connections/${connectionId}/owner-domains/${encodeURIComponent(domain)}`),
  getCrmReps: (connectionId: string) => get<CrmRep[]>(`/connections/${connectionId}/reps`),
  renameCrmRep: (connectionId: string, repId: string, displayName: string) =>
    patch<{ display_name: string }>(`/connections/${connectionId}/reps/${repId}`, {
      display_name: displayName,
    }),
  mergeCrmRep: (connectionId: string, repId: string, intoRepId: string) =>
    post<void>(`/connections/${connectionId}/reps/${repId}/merge`, { into_rep_id: intoRepId }),
  /** Move a login or dialer name to another rep; `null` splits it off on its own. */
  moveCrmRepIdentity: (connectionId: string, identityId: string, repId: string | null) =>
    put<void>(`/connections/${connectionId}/rep-identities/${identityId}`, { rep_id: repId }),

  // Agent settings (per organization)
  getAgentSettings: (orgId: string) =>
    get<AgentSettings>(`/organizations/${orgId}/agent-credentials/settings`),
  updateAgentSettings: (orgId: string, maxBudgetUsd: number | null) =>
    put<AgentSettings>(`/organizations/${orgId}/agent-credentials/settings`, {
      max_budget_usd: maxBudgetUsd,
    }),

  // Agent credentials (per organization)
  getAgentCredentials: (orgId: string) =>
    get<AgentCredential[]>(`/organizations/${orgId}/agent-credentials`),
  setAgentCredential: (orgId: string, slug: string, secret: string) =>
    put<AgentCredential>(`/organizations/${orgId}/agent-credentials/${slug}`, { secret }),
  clearAgentCredential: (orgId: string, slug: string) =>
    del<AgentCredential>(`/organizations/${orgId}/agent-credentials/${slug}`),
  testAgentCredential: (orgId: string, slug: string) =>
    post<AgentCredentialTestResult>(`/organizations/${orgId}/agent-credentials/${slug}/test`),

  // Paid contact enrichment (billed to the organization)
  listContactPurchaseProviders: (orgId: string) =>
    get<ContactPurchaseProvider[]>(`/organizations/${orgId}/contact-enrichment/providers`),
  estimateContactPurchase: (orgId: string, body: ContactPurchaseBody) =>
    post<ContactPurchaseEstimate>(`/organizations/${orgId}/contact-enrichment/estimate`, body),
  createContactPurchase: (orgId: string, body: ContactPurchaseBody) =>
    post<ContactPurchase>(`/organizations/${orgId}/contact-enrichment/jobs`, body),
  listContactPurchases: (orgId: string, params: { listId?: string; limit?: number } = {}) =>
    get<ContactPurchase[]>(`/organizations/${orgId}/contact-enrichment/jobs`, {
      collection_id: params.listId,
      limit: params.limit || undefined,
    }),
  getContactSpendLimits: (orgId: string) =>
    get<ContactSpendLimits>(`/organizations/${orgId}/contact-enrichment/limits`),
  setContactSpendLimit: (orgId: string, tenantId: string | null, monthlyLimit: number | null) =>
    put<ContactSpendLimits>(`/organizations/${orgId}/contact-enrichment/limits`, {
      tenant_id: tenantId,
      monthly_limit: monthlyLimit,
    }),
  getContactUsage: (orgId: string, month: string, groupBy: ContactUsageGroupBy) =>
    get<ContactUsage>(`/organizations/${orgId}/contact-enrichment/usage`, {
      month,
      group_by: groupBy,
    }),
  downloadContactUsageCsv: (orgId: string, month: string) =>
    downloadCsv(
      `/organizations/${orgId}/contact-enrichment/usage.csv${buildQuery({ month })}`,
      `contact-data-${month}.csv`,
    ),

  // Data providers (developer only)
  getDataProviders: () => get<DataProvider[]>("/data-providers"),
  updateDataProvider: (
    provider: string,
    body: { enabled: boolean; secret?: string | null; config: Record<string, string> },
  ) => put<DataProvider>(`/data-providers/${provider}`, body),
  clearDataProviderSecret: (provider: string) =>
    del<DataProvider>(`/data-providers/${provider}/secret`),
  testDataProvider: (provider: string) =>
    post<DataProviderTestResult>(`/data-providers/${provider}/test`),
  getDataProviderPrices: (provider: string) =>
    get<DataProviderPrice[]>(`/data-providers/${provider}/prices`),
  getDataProviderCreditRates: (provider: string) =>
    get<DataProviderCreditRate[]>(`/data-providers/${provider}/credit-rates`),
  addDataProviderCreditRate: (
    provider: string,
    body: {
      // Both for the default pack; both omitted for an organization's markup.
      package_price?: number | null;
      package_credits?: number | null;
      markup: number;
      organization_id?: string | null;
      effective_from?: string | null;
    },
  ) => post<DataProviderCreditRate>(`/data-providers/${provider}/credit-rates`, body),

  // Master Companies
  getMasterCompanyStats: () => get<MasterCompanyStats>("/master-companies/stats"),
  getFilterOptions: () => get<FilterOptions>("/master-companies/filter-options"),
  searchAiIndustryOptions: (q: string) =>
    get<string[]>("/master-companies/filter-options/ai-industries", { q }),
  searchAiServedVerticalOptions: (q: string) =>
    get<string[]>("/master-companies/filter-options/ai-served-verticals", { q }),
  searchTechnologyOptions: (q: string) =>
    get<string[]>("/master-companies/filter-options/technologies", { q }),

  getMasterCompanies: (params: CompanyListParams = {}) =>
    get<MasterCompanyListResponse>("/master-companies", {
      ...companyQuery(params),
      limit: params.limit ?? 50,
      ...(params.cursor ? { cursor: params.cursor } : { offset: params.offset ?? 0 }),
    }),
  getMasterCompany: (id: string) => get<MasterCompanyDetail>(`/master-companies/${id}`),
  getMasterContacts: (params: ContactListParams = {}) =>
    get<MasterContactListResponse>("/master-contacts", {
      name: params.name,
      role_type: params.role_type,
      company_id: params.company_id,
      has_email: params.has_email,
      sort_by: params.sort_by,
      sort_dir: params.sort_dir,
      limit: params.limit ?? 50,
      ...(params.cursor ? { cursor: params.cursor } : { offset: params.offset ?? 0 }),
    }),
  getMasterContact: (id: string) => get<MasterContactDetail>(`/master-contacts/${id}`),
  getContactChannels: (id: string) =>
    get<ContactChannelHistoryItem[]>(`/master-contacts/${id}/channels`),
  updateMasterCompany: (id: string, body: { domain?: string | null }) =>
    patch<MasterCompanyDetail>(`/master-companies/${id}`, body),
  getCompanyTechnologies: (id: string) =>
    get<CompanyTechnology[]>(`/master-companies/${id}/technologies`),
  getCompanyContacts: (id: string) => get<CompanyContact[]>(`/master-companies/${id}/contacts`),
  getCompanyEdges: (id: string) => get<CompanyEdge[]>(`/master-companies/${id}/edges`),
  getOwnershipTree: (id: string, direction: "down" | "up", maxDepth = 5) =>
    get<OwnershipTreeResponse>(`/master-companies/${id}/ownership-tree`, {
      direction,
      max_depth: maxDepth,
    }),
  getCompanySources: (id: string) =>
    get<CompanySourceRecord[]>(`/master-companies/${id}/sources`),
  getCompanyJobAds: (id: string) => get<JobAd[]>(`/master-companies/${id}/job-ads`),
  getCompanyProspectMemberships: (id: string) =>
    get<ProspectMembership[]>(`/master-companies/${id}/prospect-memberships`),
  getJobAd: (id: string) => get<JobAdDetail>(`/job-ads/${id}`),
  getJobAdFilterOptions: () => get<JobAdFilterOptions>("/job-ads/filter-options"),
  getJobAdStats: () => get<SourceStatsResponse>("/job-ads/stats"),
  getJobAds: (params: JobAdListParams = {}) =>
    get<JobAdListResponse>("/job-ads", {
      title: params.title,
      company_name: params.company_name,
      country: params.country,
      source: params.source,
      remote: params.remote,
      matched: params.matched,
      published_from: params.published_from,
      published_to: params.published_to,
      sort_by: params.sort_by,
      sort_dir: params.sort_dir,
      limit: params.limit ?? 50,
      offset: params.offset ?? 0,
    }),
  getRegulatoryFilingFilterOptions: () =>
    get<RegulatoryFilingFilterOptions>("/regulatory-filings/filter-options"),
  getRegulatoryFilingStats: () => get<SourceStatsResponse>("/regulatory-filings/stats"),
  getRegulatoryFiling: (id: string) =>
    get<RegulatoryFilingDetail>(`/regulatory-filings/${id}`),
  getRegulatoryFilings: (params: RegulatoryFilingListParams = {}) =>
    get<RegulatoryFilingListResponse>("/regulatory-filings", {
      title: params.title,
      applicant_name: params.applicant_name,
      country: params.country,
      source: params.source,
      status: params.status,
      is_active: params.is_active,
      matched: params.matched,
      filed_from: params.filed_from,
      filed_to: params.filed_to,
      hearing_from: params.hearing_from,
      hearing_to: params.hearing_to,
      min_capacity_mw: params.min_capacity_mw,
      sort_by: params.sort_by,
      sort_dir: params.sort_dir,
      limit: params.limit ?? 50,
      offset: params.offset ?? 0,
    }),
  getCompanyTenders: (companyId: string, params: { limit?: number; offset?: number } = {}) =>
    get<TenderListResponse>(`/master-companies/${companyId}/tenders`, {
      limit: params.limit,
      offset: params.offset,
    }),
  getTenderFilterOptions: () => get<TenderFilterOptions>("/tenders/filter-options"),
  getTenderStats: () => get<SourceStatsResponse>("/tenders/stats"),
  getTender: (id: string) => get<TenderDetail>(`/tenders/${id}`),
  fetchTenderDocuments: (id: string) =>
    post<{ tender_id: string; queued: boolean }>(`/tenders/${id}/fetch-documents`),
  getTenders: (params: TenderListParams = {}) =>
    get<TenderListResponse>("/tenders", {
      q: params.q,
      buyer: params.buyer,
      winner: params.winner,
      country: params.country,
      source: params.source,
      notice_type: params.notice_type,
      procedure_type: params.procedure_type,
      is_framework_agreement: params.is_framework_agreement,
      buyer_matched: params.buyer_matched,
      winner_matched: params.winner_matched,
      published_from: params.published_from,
      published_to: params.published_to,
      deadline_from: params.deadline_from,
      deadline_to: params.deadline_to,
      award_from: params.award_from,
      award_to: params.award_to,
      min_value: params.min_value,
      max_value: params.max_value,
      cpv: params.cpv,
      sort_by: params.sort_by,
      sort_dir: params.sort_dir,
      limit: params.limit ?? 50,
      offset: params.offset ?? 0,
    }),
  getCpvLookup: (codes: string[], lang = "en") => {
    const unique = Array.from(new Set(codes.filter((c) => c && c.length > 0)));
    if (unique.length === 0) {
      return Promise.resolve<CpvCodeListResponse>({ items: [] });
    }
    return get<CpvCodeListResponse>("/cpv/lookup", { codes: unique.join(","), lang });
  },
  searchCpv: (q: string, lang = "en", limit = 20) =>
    get<CpvCodeListResponse>("/cpv/search", { q, lang, limit }),
  getNewsArticleFilterOptions: () =>
    get<NewsArticleFilterOptions>("/news-articles/filter-options"),
  getNewsArticleStats: () => get<SourceStatsResponse>("/news-articles/stats"),
  getNewsArticle: (id: string) => get<NewsArticleDetail>(`/news-articles/${id}`),
  getNewsArticles: (params: NewsArticleListParams = {}) =>
    get<NewsArticleListResponse>("/news-articles", {
      q: params.q,
      source: params.source,
      source_country: params.source_country,
      language: params.language,
      domain: params.domain,
      category_id: params.category_id,
      topic_id: params.topic_id,
      keyword: params.keyword,
      extraction_status: params.extraction_status,
      story_id: params.story_id,
      published_from: params.published_from,
      published_to: params.published_to,
      min_sentiment: params.min_sentiment,
      max_sentiment: params.max_sentiment,
      sort_by: params.sort_by,
      sort_dir: params.sort_dir,
      limit: params.limit ?? 50,
      offset: params.offset ?? 0,
    }),
  // News feeds (developer only)
  getNewsFeeds: () => get<NewsFeed[]>("/news-feeds"),
  createNewsFeed: (body: NewsFeedCreate) => post<NewsFeed>("/news-feeds", body),
  updateNewsFeed: (id: string, body: NewsFeedUpdate) => patch<NewsFeed>(`/news-feeds/${id}`, body),
  deleteNewsFeed: (id: string) => del(`/news-feeds/${id}`),
  previewNewsFeed: (body: { url: string; kind: NewsFeedKind }) =>
    post<NewsFeedPreview>("/news-feeds/preview", body),
  getCompanyRegulatoryFilings: (id: string, params: CompanyRegulatoryFilingParams = {}) =>
    get<RegulatoryFilingListResponse>(`/master-companies/${id}/regulatory-filings`, {
      status: params.status,
      keyword: params.keyword,
      source: params.source,
      limit: params.limit ?? 50,
      offset: params.offset ?? 0,
    }),
  getCompanyNewsMentions: (id: string, params: { limit?: number; offset?: number } = {}) =>
    get<CompanyNewsMentionListResponse>(`/master-companies/${id}/news-mentions`, {
      limit: params.limit ?? 50,
      offset: params.offset ?? 0,
    }),
  getCompanyPressReleases: (id: string, params: { limit?: number; offset?: number } = {}) =>
    get<CompanyPressReleaseListResponse>(`/master-companies/${id}/press-releases`, {
      limit: params.limit ?? 25,
      offset: params.offset ?? 0,
    }),
  getAtsFeedFilterOptions: () => get<AtsFeedFilterOptions>("/ats-feeds/filter-options"),
  getAtsFeedCrawlStats: () => get<AtsFeedCrawlStats>("/ats-feeds/crawl-stats"),
  getAtsFeedStats: () => get<AtsFeedAtsStatsResponse>("/ats-feeds/stats"),
  getAtsFeeds: (params: AtsFeedListParams = {}) =>
    get<AtsFeedListResponse>("/ats-feeds", {
      domain: params.domain,
      ats: params.ats,
      discovery_source: params.discovery_source,
      verified: params.verified,
      is_subdomain: params.is_subdomain,
      sort_by: params.sort_by,
      sort_dir: params.sort_dir,
      limit: params.limit ?? 50,
      offset: params.offset ?? 0,
    }),
  getCompanyFinancials: (id: string) =>
    get<CompanyFinancial[]>(`/master-companies/${id}/financials`),
  getCompanyTaxAssessments: (id: string) =>
    get<CompanyTaxAssessment[]>(`/master-companies/${id}/tax-assessments`),
  getCompanyRegistryEvents: (id: string) =>
    get<CompanyRegistryEvent[]>(`/master-companies/${id}/registry-events`),
  // Streamed by the API (the buckets are private): link to it directly.
  companyFinancialFileUrl: (companyId: string, financialId: string, filename: string) =>
    `${BASE_URL}/master-companies/${companyId}/financials/${financialId}/files/${encodeURIComponent(filename)}`,
  getCrawlPages: (companyId: string) =>
    get<CrawlPagesResponse>(`/master-companies/${companyId}/crawl-pages`),
  getCrawlPageContent: (companyId: string, markdownPath: string) =>
    get<CrawlPageContentResponse>(`/master-companies/${companyId}/crawl-pages/${markdownPath}`),
  classifyCompany: (companyId: string) =>
    post<{ ai_summary: string }>(`/master-companies/${companyId}/classify`),


  // CSV Imports
  getCsvImports: () => get<CsvImport[]>("/csv-imports"),
  uploadCsvImport: (file: File, source: string, options?: { snapshotYear?: number }) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("source", source);
    if (options?.snapshotYear != null) {
      formData.append("snapshot_year", String(options.snapshotYear));
    }
    return upload<CsvImportUploadResponse>("/csv-imports/upload", formData);
  },
  retryCsvImport: (id: string) => post<CsvImport>(`/csv-imports/${id}/retry`),

  // Background Jobs
  getBackgroundJobs: () => get<BackgroundJob[]>("/background-jobs"),
  updateBackgroundJob: (key: string, enabled: boolean) =>
    put<BackgroundJob>(`/background-jobs/${key}`, { enabled }),

  // System Prompts
  getSystemPrompts: () => get<SystemPrompt[]>("/system-prompts"),
  updateSystemPrompt: (slug: string, text: string) =>
    put<SystemPrompt>(`/system-prompts/${slug}`, { text }),
  resetSystemPrompt: (slug: string) => del(`/system-prompts/${slug}`),

  // Dev tools
  getRolesPermissions: () => get<RolesPermissionsResponse>("/dev/roles-permissions"),
  getTableStats: () => get<TableStats[]>("/dev/stats/tables"),
  getAiGenerationLogs: (params: {
    failures_only?: boolean;
    table_name?: string;
    field_group?: string;
    method?: string;
    limit?: number;
    offset?: number;
  }) =>
    get<AiGenerationLogsResponse>("/dev/ai-generation-logs", {
      failures_only: params.failures_only,
      table_name: params.table_name,
      field_group: params.field_group,
      method: params.method,
      limit: params.limit,
      offset: params.offset,
    }),


  // Platform health: every import's runs and what it brought in
  getPlatformHealth: () => get<PlatformHealth>("/platform/health"),
  getPlatformQueues: () => get<PlatformQueues>("/platform/queues"),
  getPlatformSignalWorkflows: () => get<PlatformSignalWorkflows>("/platform/signal-workflows"),
  getPlatformRecurring: () => get<PlatformRecurring>("/platform/recurring"),
  getPlatformRecurringTicks: (job: string, key: string | null) =>
    get<PlatformRecurringTicks>(
      `/platform/recurring/${encodeURIComponent(job)}/ticks${key ? `?key=${encodeURIComponent(key)}` : ""}`,
    ),
  getDataProviderUsage: (month: string) =>
    get<DataProviderUsage>("/platform/data-provider-usage", { month }),
  getDataProviderLimits: () => get<DataProviderLimits>("/platform/data-provider-limits"),

  // Prospect Lists
  getProspectLists: (tenantId: string) =>
    get<ProspectList[]>("/collections", { tenant_id: tenantId }),
  getProspectListsOverview: (tenantIds: string[], includeArchived = false) =>
    get<ProspectListOverview[]>("/collections/overview", {
      tenant_ids: tenantIds,
      include_archived: includeArchived || undefined,
    }),
  getProspectList: (listId: string, { includeEntries = true }: { includeEntries?: boolean } = {}) =>
    get<ProspectListDetail>(`/collections/${listId}`, includeEntries ? undefined : { include_entries: false }),
  createProspectList: (data: { tenant_id: string; name: string; description?: string }) =>
    post<ProspectList>("/collections", data),
  updateProspectList: (
    listId: string,
    data: {
      name?: string;
      description?: string;
      view_settings?: ProspectListViewSettings;
      archived?: boolean;
    },
  ) => patch<ProspectList>(`/collections/${listId}`, data),
  deleteProspectList: (listId: string) => del(`/collections/${listId}`),
  addProspectsToList: (listId: string, masterCompanyIds: string[]) =>
    post<{ added: number }>(`/collections/${listId}/prospects`, {
      master_company_ids: masterCompanyIds,
    }),
  /** Listed unresolved unless its org number or domain finds the company. */
  addUnresolvedProspect: (listId: string, company: UnresolvedProspectInput) =>
    post<{ added: number }>(`/collections/${listId}/prospects`, { unresolved: [company] }),
  bulkAddProspectsFromFilter: (listId: string, filters: CompanyListParams) => {
    const {
      sort_by: _sort_by,
      sort_dir: _sort_dir,
      limit: _limit,
      offset: _offset,
      cursor: _cursor,
      ...filterOnly
    } = filters;
    return post<BulkAddJob>(`/collections/${listId}/bulk-add-from-filter`, {
      filters: filterOnly,
    });
  },
  removeProspectFromList: (listId: string, entryId: string) =>
    del(`/collections/${listId}/prospects/${entryId}`),
  disqualifyProspect: (listId: string, entryId: string, reason: string | null) =>
    post<ProspectListEntry>(`/collections/${listId}/prospects/${entryId}/disqualify`, {
      reason,
    }),
  getLinkSuggestions: (listId: string, entryId: string) =>
    get<LinkSuggestion[]>(`/collections/${listId}/prospects/${entryId}/link-suggestions`),
  linkProspect: (listId: string, entryId: string, masterCompanyId: string) =>
    post<ProspectListEntry>(`/collections/${listId}/prospects/${entryId}/link`, {
      master_company_id: masterCompanyId,
    }),
  acceptProspect: (listId: string, entryId: string) =>
    post<ProspectListEntry>(`/collections/${listId}/prospects/${entryId}/accept`),
  resetProspectStatus: (listId: string, entryId: string) =>
    post<ProspectListEntry>(`/collections/${listId}/prospects/${entryId}/reset`),
  updateProspectLabels: (
    listId: string,
    entryId: string,
    data: { set_labels?: Record<string, string>; remove_label_keys?: string[] },
  ) => patch<ProspectListEntry>(`/collections/${listId}/prospects/${entryId}/labels`, data),
  getEnrichMasterFromLabelsPreview: (listId: string) =>
    get<EnrichMasterFromLabelsPreview>(
      `/collections/${listId}/actions/enrich-master-from-labels/preview`,
    ),
  enrichMasterFromLabels: (listId: string, entryIds: string[]) =>
    post<EnrichMasterFromLabelsResult>(
      `/collections/${listId}/actions/enrich-master-from-labels`,
      { entry_ids: entryIds },
    ),

  startCrmExport: (listId: string, data: CrmExportRequest) =>
    post<CrmExportJob>(`/collections/${listId}/crm-export`, data),
  getLatestCrmExportJob: (listId: string) =>
    get<CrmExportJob | null>(`/collections/${listId}/crm-export-jobs/latest`),
  getProspectListCrmStatus: (listId: string) =>
    get<ProspectListCrmStatus>(`/collections/${listId}/crm-status`),
  /** Just the connection a list exports into — no per-contact rows. */
  getProspectListCrmConnection: (listId: string) =>
    get<{ connection_id: string; provider: string }>(`/collections/${listId}/crm-connection`),
  getConnectionSequences: (connectionId: string) =>
    get<CrmSequenceOption[]>(`/connections/${connectionId}/sequences`),

  // Contacts: dedup-aware create/resolve + contacts on prospect lists
  resolveContact: (data: ContactInput) =>
    post<ContactResolveResponse>("/master-contacts/resolve", data),
  createMasterContact: (
    data: ContactInput & { postal_code?: string; city?: string; country?: string },
  ) => post<MasterContactDetail>("/master-contacts", data),
  getProspectContacts: (listId: string) =>
    get<ProspectListContactEntry[]>(`/collections/${listId}/contacts`),
  addContactToList: (listId: string, data: AddContactPayload) =>
    post<ProspectListContactEntry>(`/collections/${listId}/contacts`, data),
  updateProspectContact: (
    listId: string,
    entryId: string,
    data: { note?: string | null; set_labels?: Record<string, string>; remove_label_keys?: string[] },
  ) => patch<ProspectListContactEntry>(`/collections/${listId}/contacts/${entryId}`, data),
  removeProspectContact: (listId: string, entryId: string) =>
    del(`/collections/${listId}/contacts/${entryId}`),
  acceptProspectContact: (listId: string, entryId: string) =>
    post<ProspectListContactEntry>(`/collections/${listId}/contacts/${entryId}/accept`),
  disqualifyProspectContact: (listId: string, entryId: string, reason: string | null) =>
    post<ProspectListContactEntry>(`/collections/${listId}/contacts/${entryId}/disqualify`, {
      reason,
    }),
  resetProspectContact: (listId: string, entryId: string) =>
    post<ProspectListContactEntry>(`/collections/${listId}/contacts/${entryId}/reset`),

  // CSV Exports
  exportMasterCompanies: (
    params: CompanyListParams = {},
    exportFields?: string[],
    roleColumns?: string[],
  ) => {
    const query = buildQuery({
      ...companyQuery(params),
      fields: exportFields,
      role_columns: roleColumns,
    });
    return downloadCsv(`/master-companies/export${query}`, "companies.csv");
  },
  exportProspectList: (
    listId: string,
    listName: string,
    exportFields?: string[],
    roleColumns?: string[],
  ) => {
    const safeName = listName.replace(/\s+/g, "-").replace(/"/g, "");
    const query = buildQuery({ fields: exportFields, role_columns: roleColumns });
    return downloadCsv(`/collections/${listId}/export${query}`, `prospects-${safeName}.csv`);
  },
  exportCompaniesMissingWebsite: (countries: string[]) =>
    downloadCsv(
      `/dev/export/companies-missing-website${buildQuery({ country: countries })}`,
      "companies-missing-website.parquet",
    ),

  // Workflow logs

  // Organizations
  getOrganizations: () => get<Organization[]>("/organizations"),
  createOrganization: (data: { name: string }) => post<Organization>("/organizations", data),
  getOrganization: (id: string) => get<Organization>(`/organizations/${id}`),
  getOrgDashboard: (orgId: string, since?: string) =>
    get<OrgDashboard>(`/organizations/${orgId}/dashboard`, { since }),
  updateOrganization: (id: string, data: OrganizationUpdate) =>
    patch<Organization>(`/organizations/${id}`, data),
  deleteOrganization: (id: string) => del(`/organizations/${id}`),
  getFeatureFlags: () => get<FeatureFlagDefinition[]>("/organizations/feature-flags"),
  setOrganizationFeature: (orgId: string, key: string, enabled: boolean) =>
    put<Organization>(`/organizations/${orgId}/features/${key}`, { enabled }),

  // Organization Members
  getOrgMembers: (orgId: string) => get<OrgMember[]>(`/organizations/${orgId}/members`),
  updateOrgMember: (orgId: string, userId: string, data: { roles: string[] }) =>
    patch<OrgMember>(`/organizations/${orgId}/members/${userId}`, data),
  removeOrgMember: (orgId: string, userId: string) =>
    del(`/organizations/${orgId}/members/${userId}`),

  // Organization Invitations — an existing account is added straight away.
  getOrgInvitations: (orgId: string) => get<Invitation[]>(`/organizations/${orgId}/invitations`),
  inviteOrgMember: (orgId: string, data: { email: string; roles: string[] }) =>
    post<OrgInviteResult>(`/organizations/${orgId}/invitations`, data),
  cancelOrgInvitation: (orgId: string, invitationId: string) =>
    del(`/organizations/${orgId}/invitations/${invitationId}`),

  // User Groups (org-scoped) — used to grant restricted micro apps.
  getUserGroups: (orgId: string) => get<UserGroup[]>(`/organizations/${orgId}/groups`),
  createUserGroup: (orgId: string, data: { name: string; description?: string }) =>
    post<UserGroup>(`/organizations/${orgId}/groups`, data),
  addUserGroupMember: (orgId: string, groupId: string, userId: string) =>
    post<UserGroupMember>(`/organizations/${orgId}/groups/${groupId}/members`, {
      user_id: userId,
    }),
  removeUserGroupMember: (orgId: string, groupId: string, userId: string) =>
    del(`/organizations/${orgId}/groups/${groupId}/members/${userId}`),

  // Saved filter views: the caller's private views in this exact context, then
  // views shared with `tenant_id` / `organization_id`.
  getSavedViews: (params: {
    page_key: string;
    tenant_id?: string | null;
    organization_id?: string | null;
  }) => get<SavedView[]>("/saved-views", params),
  createSavedView: (data: SavedViewCreate) => post<SavedView>("/saved-views", data),
  updateSavedView: (id: string, data: SavedViewUpdate) =>
    patch<SavedView>(`/saved-views/${id}`, data),
  deleteSavedView: (id: string) => del(`/saved-views/${id}`),

  // Slack
  getSlackIntegration: async (orgId: string): Promise<SlackIntegration | null> => {
    try {
      return await get<SlackIntegration>(`/slack/integrations/${orgId}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },
  deleteSlackIntegration: (orgId: string) => del(`/slack/integrations/${orgId}`),
  slackInstallUrl: (orgId: string) =>
    `${BASE_URL}/slack/install${buildQuery({ organization_id: orgId })}`,

  // Notion (one org's workspaces; reads need org:read, the rest org:manage_credentials)
  getNotionSetupInfo: (orgId: string) =>
    get<NotionSetupInfo>(`/organizations/${orgId}/notion/setup-info`),
  // Navigate the browser here: it redirects to Notion's consent screen, which
  // comes back to /organization/integrations?notion=connected|error|cancelled.
  notionInstallUrl: (orgId: string) => `${BASE_URL}/organizations/${orgId}/notion/install`,
  getNotionConnections: (orgId: string) =>
    get<NotionConnection[]>(`/organizations/${orgId}/notion/connections`),
  deleteNotionConnection: (orgId: string, connectionId: string) =>
    del(`/organizations/${orgId}/notion/connections/${connectionId}`),
  // Pages and databases shared with the app, searched live by title.
  searchNotionObjects: (orgId: string, connectionId: string, query: string) =>
    get<NotionObject[]>(`/organizations/${orgId}/notion/connections/${connectionId}/objects`, {
      query,
    }),
  getNotionScopes: (orgId: string, connectionId: string, tenantId: string) =>
    get<NotionScope[]>(`/organizations/${orgId}/notion/connections/${connectionId}/scopes`, {
      tenant_id: tenantId,
    }),
  // Replaces every scope the tenant has on this connection.
  replaceNotionScopes: (
    orgId: string,
    connectionId: string,
    tenantId: string,
    scopes: NotionScopeInput[],
  ) =>
    put<NotionScope[]>(
      `/organizations/${orgId}/notion/connections/${connectionId}/scopes${buildQuery({
        tenant_id: tenantId,
      })}`,
      { scopes },
    ),

  // Organization Tenants
  getOrgTenants: (orgId: string) => get<OrgTenant[]>(`/organizations/${orgId}/tenants`),
  assignOrgTenant: (orgId: string, data: { tenant_id: string }) =>
    post<OrgTenant>(`/organizations/${orgId}/tenants`, data),
  unassignOrgTenant: (orgId: string, tenantId: string) =>
    del(`/organizations/${orgId}/tenants/${tenantId}`),
  // ownerOrgId is the tenant's current owner; only its admins (or site admins)
  // can hand the tenant to another org it is already linked to.
  setTenantPrimaryOrganization: (ownerOrgId: string, tenantId: string, newOwnerOrgId: string) =>
    put<OrgTenant>(`/organizations/${ownerOrgId}/tenants/${tenantId}/primary`, {
      organization_id: newOwnerOrgId,
    }),

  // Training data exports
  listTrainingDataExports: () => get<TrainingDataExport[]>("/training-data-exports"),
  createTrainingDataExport: (body: TrainingDataExportCreate) =>
    post<TrainingDataExport>("/training-data-exports", body),
  trainingDataExportDownloadUrl: (id: string) =>
    `${BASE_URL}/training-data-exports/${id}/download`,

  // Agent Skills (MCP)
  getAgentSkills: (params?: {
    tenant_id?: string | null;
    include_global?: boolean;
    enabled_only?: boolean;
  }) =>
    get<AgentSkill[]>("/agent-skills", {
      tenant_id: params?.tenant_id,
      include_global: params?.include_global,
      enabled_only: params?.enabled_only,
    }),
  createAgentSkill: (body: AgentSkillCreate) => post<AgentSkill>("/agent-skills", body),
  updateAgentSkill: (id: string, body: AgentSkillUpdate) =>
    patch<AgentSkill>(`/agent-skills/${id}`, body),
  deleteAgentSkill: (id: string) => del(`/agent-skills/${id}`),

  // Micro apps (agent-published dashboards). Slugs are unique across every
  // tenant, so a slug alone names exactly one app; the app's own tenant_id
  // still governs who may see it, resolved server-side from the session.
  getMicroApps: (params?: {
    tenant_id?: string | null;
    organization_id?: string | null;
    include_global?: boolean;
    enabled_only?: boolean;
  }) =>
    get<MicroApp[]>("/micro-apps", {
      tenant_id: params?.tenant_id,
      organization_id: params?.organization_id,
      include_global: params?.include_global,
      enabled_only: params?.enabled_only,
    }),
  // Apps the caller may manage (write), regardless of access_mode. The admin
  // surface uses this rather than the consumer list, which hides restricted
  // apps the caller isn't in a granted group for.
  getManageableMicroApps: (organizationId?: string | null) =>
    get<MicroAppManaged[]>("/micro-apps/manage", { organization_id: organizationId }),
  // Group access grants for a restricted app.
  getMicroAppGrants: (appId: string) => get<MicroAppGrant[]>(`/micro-apps/${appId}/grants`),
  addMicroAppGrant: (appId: string, groupId: string) =>
    post<MicroAppGrant>(`/micro-apps/${appId}/grants`, { group_id: groupId }),
  removeMicroAppGrant: (appId: string, groupId: string) =>
    del(`/micro-apps/${appId}/grants/${groupId}`),
  // TV sharing: a public `dash.sxq.no/<code>` link. PUT enables or rotates.
  getMicroAppTvShare: (appId: string) => get<MicroAppTvShare>(`/micro-apps/${appId}/tv-share`),
  enableMicroAppTvShare: (appId: string) => put<MicroAppTvShare>(`/micro-apps/${appId}/tv-share`),
  disableMicroAppTvShare: (appId: string) => del(`/micro-apps/${appId}/tv-share`),
  getMicroAppVersions: (appId: string) =>
    get<MicroAppVersion[]>(`/micro-apps/${appId}/versions`),
  // knownVersion is the version already mounted in the caller's sandbox iframe.
  // Sending it suppresses a fresh document_url, so the frame is kept and the
  // data pushed into it instead of the app hard-reloading every refresh.
  renderMicroApp: (slug: string, knownVersion?: number | null) =>
    post<MicroAppRender>(
      `/micro-apps/${slug}/render${buildQuery({ known_version: knownVersion })}`,
    ),
  updateMicroApp: (id: string, body: MicroAppUpdate) => patch<MicroApp>(`/micro-apps/${id}`, body),
  deleteMicroApp: (id: string) => del(`/micro-apps/${id}`),
  // Upload path for apps too large to publish over MCP in one tool call. With
  // `initial_version` the app and its first version are created atomically.
  createMicroApp: (body: MicroAppCreate) => post<MicroAppCreateResponse>("/micro-apps", body),
  publishMicroAppVersion: (slug: string, body: MicroAppPublishBody) =>
    post<MicroAppPublishResponse>(`/micro-apps/${slug}/versions`, body),

  // Workflows + signals
  getWorkflowTriggerTypes: () => get<WorkflowTriggerType[]>("/workflows/trigger-types"),
  // With a tenant: each data type's per-tenant choices and whether it is usable.
  getWorkflowDataTypes: (tenantId?: string | null) =>
    get<WorkflowDataType[]>("/workflows/data-types", { tenant_id: tenantId }),
  getWorkflowStepTypes: () => get<WorkflowStepType[]>("/workflows/step-types"),
  // Agent definitions (writes are developer-only)
  getAgentDefinitions: (tenantId?: string | null) =>
    get<AgentDefinition[]>("/agent-definitions", { tenant_id: tenantId }),
  getAgentTools: () => get<AgentToolOption[]>("/agent-definitions/tools"),
  getAgentContracts: () => get<AgentContractOption[]>("/agent-definitions/contracts"),
  // With a tenant: as listed for the organization that pays for its agent runs.
  getAgentModels: (tenantId?: string | null) =>
    get<AgentModelCatalog>("/agent-definitions/models", { tenant_id: tenantId }),
  // With a tenant: whether each source is set up for the organization that pays.
  getAgentDataSources: (tenantId?: string | null) =>
    get<DataSourceOption[]>("/agent-definitions/data-sources", { tenant_id: tenantId }),
  createAgentDefinition: (body: AgentDefinitionCreate) =>
    post<AgentDefinition>("/agent-definitions", body),
  updateAgentDefinition: (id: string, body: AgentDefinitionUpdate) =>
    patch<AgentDefinition>(`/agent-definitions/${id}`, body),
  cloneAgentDefinition: (id: string, body: { tenant_id: string; name?: string }) =>
    post<AgentDefinition>(`/agent-definitions/${id}/clone`, body),
  deleteAgentDefinition: (id: string) => del(`/agent-definitions/${id}`),
  getAgentFeedback: (
    id: string,
    params: { tenant_id?: string | null; limit?: number; offset?: number },
  ) =>
    get<AgentFeedbackPage>(`/agent-definitions/${id}/feedback`, {
      tenant_id: params.tenant_id,
      limit: params.limit || undefined,
      offset: params.offset || undefined,
    }),
  redistillAgentFeedback: (id: string, tenantId?: string | null) =>
    post<{ status: string }>(
      `/agent-definitions/${id}/feedback/redistill${buildQuery({ tenant_id: tenantId })}`,
    ),
  getSlackChannels: (tenantId: string) =>
    get<SlackChannels>("/workflows/slack-channels", { tenant_id: tenantId }),
  getWorkflows: (params?: {
    tenant_id?: string | null;
    trigger_type?: string;
    data_type?: string;
    active_only?: boolean;
  }) =>
    get<Workflow[]>("/workflows", {
      tenant_id: params?.tenant_id,
      trigger_type: params?.trigger_type,
      data_type: params?.data_type,
      active_only: params?.active_only,
    }),
  getWorkflow: (id: string) => get<Workflow>(`/workflows/${id}`),
  createWorkflow: (body: WorkflowCreate) => post<Workflow>("/workflows", body),
  updateWorkflow: (id: string, body: WorkflowUpdate) => patch<Workflow>(`/workflows/${id}`, body),
  deleteWorkflow: (id: string) => del(`/workflows/${id}`),
  getWorkflowScheduleRuns: (id: string, limit = 20) =>
    get<WorkflowScheduleRun[]>(`/workflows/${id}/schedule-runs`, { limit }),
  runWorkflowNow: (id: string) => post<WorkflowScheduleRun>(`/workflows/${id}/run-now`),
  getSignals: (params?: SignalFilters & { limit?: number; offset?: number }) =>
    get<Signal[]>("/signals", {
      ...signalQuery(params ?? {}),
      limit: params?.limit,
      offset: params?.offset,
    }),
  locateSignal: (signalId: string, filters: SignalFilters) =>
    get<SignalLocation>(`/signals/${signalId}/locate`, signalQuery(filters)),
  countSignals: (filters: SignalFilters) =>
    get<SignalCount>("/signals/count", signalQuery(filters)),
  // The countries the tenant's matched signals come from (ISO alpha-2).
  getSignalCountries: (tenantId: string | null) =>
    get<string[]>("/signals/countries", { tenant_id: tenantId }),
  getSignalDashboard: (params: SignalDashboardParams) =>
    get<SignalDashboard>("/signals/dashboard", {
      tenant_id: params.tenant_id,
      workflow_id: params.workflow_id,
      since: params.since,
    }),
  // Run every step waiting for a person on these signals; per-signal outcome.
  continueSignals: (signalIds: string[]) =>
    post<SignalContinueResponse>("/signals/continue", { signal_ids: signalIds }),
  getSignalResearch: (signalId: string) => get<SignalResearch>(`/signals/${signalId}/research`),
  startSignalResearch: (signalId: string) => post<AgentRun>(`/signals/${signalId}/research`),
  // Run a workflow step waiting for a person to continue on the signal. 409
  // when the step was decided already or cannot run right now.
  approveSignalStep: (signalId: string, executionId: string) =>
    post<StepExecution>(`/signals/${signalId}/executions/${executionId}/approve`),
  getSignalResearchTranscript: (signalId: string, runId: string, afterSeq = 0) =>
    get<AgentRunTranscript>(`/signals/${signalId}/research/${runId}/transcript`, {
      after_seq: afterSeq,
    }),

  // --- The tenant's prospects ---
  getProspects: (params: ProspectFilters & { limit?: number; offset?: number }) =>
    get<Prospect[]>("/prospects", {
      ...prospectQuery(params),
      limit: params.limit,
      offset: params.offset,
    }),
  countProspects: (filters: ProspectFilters) =>
    get<ProspectCount>("/prospects/count", prospectQuery(filters)),
  locateProspect: (prospectId: string, filters: ProspectFilters) =>
    get<ProspectLocation>(`/prospects/${prospectId}/locate`, prospectQuery(filters)),
  getProspect: (prospectId: string) => get<ProspectDetail>(`/prospects/${prospectId}`),
  getProspectCountries: (tenantId: string) =>
    get<string[]>("/prospects/countries", { tenant_id: tenantId }),
  getProspectRunOptions: (params: { tenant_id: string; collection_id?: string; q?: string; ids?: string[] }) =>
    get<ProspectRunOption[]>("/prospects/agent-runs", params),
  getProspectDashboard: (tenantId: string, since?: string) =>
    get<ProspectDashboard>("/prospects/dashboard", { tenant_id: tenantId, since }),
  getProspectHitTranscript: (prospectId: string, hitId: string, afterSeq = 0) =>
    get<AgentRunTranscript>(`/prospects/${prospectId}/hits/${hitId}/transcript`, {
      after_seq: afterSeq,
    }),
  reviewProspects: (body: ProspectReviewRequest) =>
    post<ProspectReviewResponse>("/prospects/review", body),
  continueProspects: (prospectIds: string[]) =>
    post<ProspectContinueResponse>("/prospects/continue", { prospect_ids: prospectIds }),
  snoozeProspects: (prospectIds: string[], until: string | null) =>
    post<ProspectUpdateResponse>("/prospects/snooze", { prospect_ids: prospectIds, until }),
  markProspectsDone: (prospectIds: string[], done: boolean) =>
    post<ProspectUpdateResponse>("/prospects/done", { prospect_ids: prospectIds, done }),

  // --- Micro app Google Sheets ---
  getMicroAppGoogleSetupInfo: () => get<GoogleSetupInfo>("/micro-app-sheets/setup-info"),
  getMicroAppGoogleConnections: (organizationId?: string | null) =>
    get<MicroAppGoogleConnection[]>("/micro-app-sheets/connections", {
      organization_id: organizationId,
    }),
  disconnectMicroAppGoogle: (id: string) => del(`/micro-app-sheets/connections/${id}`),
  getMicroAppSheetSources: (organizationId?: string | null) =>
    get<MicroAppSheetSource[]>("/micro-app-sheets/sources", { organization_id: organizationId }),
  createMicroAppSheetSource: (body: {
    organization_id: string;
    tenant_id?: string | null;
    slug: string;
    title: string;
    spreadsheet_id: string;
    google_connection_id: string;
    allow_all?: boolean;
    allowed_ranges?: string[];
  }) => post<MicroAppSheetSource>("/micro-app-sheets/sources", body),
  updateMicroAppSheetSource: (
    id: string,
    body: Partial<{
      title: string;
      spreadsheet_id: string;
      google_connection_id: string;
      allow_all: boolean;
      allowed_ranges: string[];
      tenant_id: string | null;
    }>,
  ) => patch<MicroAppSheetSource>(`/micro-app-sheets/sources/${id}`, body),
  deleteMicroAppSheetSource: (id: string) => del(`/micro-app-sheets/sources/${id}`),
  inspectSpreadsheet: (
    organization_id: string,
    google_connection_id: string,
    spreadsheet: string,
  ) =>
    post<SheetInspectResult>("/micro-app-sheets/sources/inspect", {
      organization_id,
      google_connection_id,
      spreadsheet,
    }),
  /** Browser navigation target (not fetch) — starts the Google consent flow. */
  microAppGoogleInstallUrl: (organizationId: string, sourceId?: string) =>
    `${BASE_URL}/micro-app-sheets/install${buildQuery({
      organization_id: organizationId,
      source_id: sourceId,
    })}`,
  testMicroAppSheetSource: (id: string) =>
    post<SheetSourceTestResult>(`/micro-app-sheets/sources/${id}/test`),

  // Prospect Orders (org-scoped)
  getProspectOrders: (orgId: string, params: ProspectOrderListParams = {}) =>
    get<ProspectOrderListResponse>(`/organizations/${orgId}/prospect-orders`, {
      q: params.q,
      status: params.status,
      tenant_id: params.tenant_id,
      assignee_user_id: params.assignee_user_id,
      sort_by: params.sort_by,
      sort_dir: params.sort_dir,
      limit: params.limit ?? 50,
      offset: params.offset ?? 0,
    }),
  getProspectOrder: (orgId: string, orderId: string) =>
    get<ProspectOrderDetail>(`/organizations/${orgId}/prospect-orders/${orderId}`),
  createProspectOrder: (orgId: string, data: ProspectOrderCreate) =>
    post<ProspectOrderDetail>(`/organizations/${orgId}/prospect-orders`, data),
  updateProspectOrder: (orgId: string, orderId: string, data: ProspectOrderUpdate) =>
    patch<ProspectOrderDetail>(`/organizations/${orgId}/prospect-orders/${orderId}`, data),
  deleteProspectOrder: (orgId: string, orderId: string) =>
    del(`/organizations/${orgId}/prospect-orders/${orderId}`),
  addProspectOrderList: (orgId: string, orderId: string, listId: string) =>
    post<ProspectOrderDetail>(`/organizations/${orgId}/prospect-orders/${orderId}/collections`, {
      collection_id: listId,
    }),
  removeProspectOrderList: (orgId: string, orderId: string, listId: string) =>
    del(`/organizations/${orgId}/prospect-orders/${orderId}/collections/${listId}`),
  addProspectOrderComment: (orgId: string, orderId: string, body: string) =>
    post<ProspectOrderComment>(`/organizations/${orgId}/prospect-orders/${orderId}/comments`, {
      body,
    }),
  getProspectOrderAgentRuns: (orgId: string, orderId: string) =>
    get<AgentRun[]>(`/organizations/${orgId}/prospect-orders/${orderId}/agent-runs`),
  getProspectOrderAgentRunTranscript: (
    orgId: string,
    orderId: string,
    runId: string,
    afterSeq = 0,
  ) =>
    get<AgentRunTranscript>(
      `/organizations/${orgId}/prospect-orders/${orderId}/agent-runs/${runId}/transcript`,
      { after_seq: afterSeq },
    ),
  // The tenant's prospect-order workflows and what each did on this order.
  getProspectOrderWorkflows: (orgId: string, orderId: string) =>
    get<ProspectOrderWorkflows>(`/organizations/${orgId}/prospect-orders/${orderId}/workflows`),
  approveProspectOrderStep: (orgId: string, orderId: string, executionId: string) =>
    post<StepExecution>(
      `/organizations/${orgId}/prospect-orders/${orderId}/executions/${executionId}/approve`,
    ),
  // What "Run agent" would run for the tenant, and the models the owner's plan can pick.
  getAgentRunPlan: (orgId: string, tenantId: string) =>
    get<AgentRunPlan>(`/organizations/${orgId}/prospect-orders/agent-run-plan`, {
      tenant_id: tenantId,
    }),
  // Place an order and run an agent on it now ("Run agent").
  runAgentOnNewOrder: (orgId: string, data: ProspectOrderAgentRunCreate) =>
    post<ProspectOrderAgentRunResult>(`/organizations/${orgId}/prospect-orders/run-agent`, data),
  // Re-run the tenant's prospect-order workflows (or one) on this order.
  runProspectOrderWorkflows: (orgId: string, orderId: string, workflowId?: string) =>
    post<ProspectOrderRunResult>(`/organizations/${orgId}/prospect-orders/${orderId}/run`, {
      workflow_id: workflowId ?? null,
    }),

  // Merge suggestions
  getMergeSuggestions: (params: {
    status?: string;
    suggestion_type?: string;
    limit?: number;
    offset?: number;
  }) =>
    get<MergeSuggestionListResponse>("/merge-suggestions", {
      status: params.status,
      suggestion_type: params.suggestion_type,
      limit: params.limit || undefined,
      offset: params.offset || undefined,
    }),
  getMergeSuggestionStats: () => get<MergeSuggestionStats>("/merge-suggestions/stats"),
  approveMergeSuggestion: (id: string) =>
    post<{ merged_company_id: string }>(`/merge-suggestions/${id}/approve`),
  rejectMergeSuggestion: (id: string) => post<void>(`/merge-suggestions/${id}/reject`),
  bulkApproveMergeSuggestions: (ids: string[]) =>
    post<{ processed: number; errors: { id: string; error: string }[] }>(
      "/merge-suggestions/bulk-approve",
      { ids },
    ),
  bulkRejectMergeSuggestions: (ids: string[]) =>
    post<{ processed: number; errors: { id: string; error: string }[] }>(
      "/merge-suggestions/bulk-reject",
      { ids },
    ),
  splitCompany: (companyId: string, body: { source_record_ids: string[]; job_ad_ids: string[] }) =>
    post<{ new_company_id: string }>(`/master-companies/${companyId}/split`, body),

  // Inference instances
  listInferenceInstances: (status?: string) =>
    get<InferenceInstance[]>("/inference-instances", { status }),
  approveInferenceInstance: (id: string) =>
    post<InferenceInstance>(`/inference-instances/${id}/approve`),
  rejectInferenceInstance: (id: string, reason?: string) =>
    post<InferenceInstance>(`/inference-instances/${id}/reject`, { reason: reason ?? null }),
  updateInferenceInstance: (
    id: string,
    body: {
      weight?: number;
      bulk_only?: boolean;
      interactive_only?: boolean;
      api_url?: string;
      api_key?: string;
    },
  ) => patch<InferenceInstance>(`/inference-instances/${id}`, body),
  deleteInferenceInstance: (id: string) => del(`/inference-instances/${id}`),
  getInferenceInstallLink: (fleet: string) =>
    get<InferenceInstallLink>("/inference-instances/install-link", { fleet }),
  listInferenceFleets: () => get<InferenceFleet[]>(`/inference-instances/fleets`),

  // Rented GPU boxes (VAST.ai)
  listInferenceRentals: () => get<InferenceRental[]>("/inference-rentals"),
  listInferenceRentalOffers: (machine?: string) =>
    get<InferenceRentalOffer[]>("/inference-rentals/offers", { machine }),
  createInferenceRental: (offerId: number) =>
    post<InferenceRental>("/inference-rentals", { offer_id: offerId }),
  destroyInferenceRental: (id: string) =>
    post<InferenceRental>(`/inference-rentals/${id}/destroy`),
  getInferenceRentalCredentials: () =>
    get<InferenceRentalCredentials>("/inference-rentals/credentials"),
  putInferenceRentalCredentials: (apiKey: string) =>
    put<InferenceRentalCredentials>("/inference-rentals/credentials", { api_key: apiKey }),
  deleteInferenceRentalCredentials: () => del("/inference-rentals/credentials"),
};

export type ProspectOrderStatus =
  | "new"
  | "triaged"
  | "in_progress"
  | "delivered"
  | "closed"
  | "cancelled";

export interface ProspectOrderListItem {
  id: string;
  organization_id: string;
  tenant_id: string;
  tenant_name: string | null;
  title: string;
  status: string;
  requested_count: number | null;
  deadline: string | null;
  collection_count: number;
  assignees: UserBrief[];
  comment_count: number;
  // Set when a workflow made the order; null for an order a person placed.
  origin: ProspectOrderOrigin | null;
  // Runs still in flight on it (pending, queued or running).
  active_agent_runs: ActiveAgentRun[];
  created_at: string;
  updated_at: string;
}

// The workflow step that made an order, and the signal it was drafted from.
export interface ProspectOrderOrigin {
  workflow_id: string | null;
  workflow_name: string | null;
  step_id: string | null;
  signal_id: string | null;
  data_type: string | null;
  record_title: string | null;
  record_url: string | null;
}

export interface ProspectOrderListSummary {
  id: string;
  name: string;
  prospect_count: number;
  pending_count: number;
  signal_trigger_count: number;
  last_added_at: string | null;
}

export interface ProspectOrder extends ProspectOrderListItem {
  // For links into the tenant's workflow pages.
  tenant_slug: string | null;
  // The brief, named like the tenant profile; null = the tenant's own.
  offering_text: string | null;
  icp_text: string | null;
  buyer_persona_text: string | null;
  collections: ProspectOrderListSummary[];
  created_by: UserBrief | null;
  // The model picked under "Run agent" for every agent run on the order.
  agent_model: string | null;
  // Only companies the tenant never prospected before; repeats are refused.
  new_prospects_only: boolean;
}

export interface ProspectOrderComment {
  id: string;
  prospect_order_id: string;
  body: string;
  author: UserBrief | null;
  created_at: string;
}

export interface ProspectOrderDetail extends ProspectOrder {
  comments: ProspectOrderComment[];
}

export type ProspectOrderListResponse = Paginated<ProspectOrderListItem>;

export interface ProspectOrderListParams {
  q?: string;
  status?: string[];
  tenant_id?: string;
  assignee_user_id?: string;
  sort_by?: string;
  sort_dir?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

export interface ProspectOrderCreate {
  tenant_id: string;
  title: string;
  status?: ProspectOrderStatus;
  offering_text?: string | null;
  icp_text?: string | null;
  buyer_persona_text?: string | null;
  requested_count?: number | null;
  deadline?: string | null;
  new_prospects_only?: boolean;
  assignee_user_ids?: string[];
}

export interface ProspectOrderUpdate {
  title?: string;
  status?: ProspectOrderStatus;
  offering_text?: string | null;
  icp_text?: string | null;
  buyer_persona_text?: string | null;
  requested_count?: number | null;
  deadline?: string | null;
  new_prospects_only?: boolean;
  assignee_user_ids?: string[];
}

// What one workflow step did on a signal, an order or a scheduled run, as its page shows it.
export interface StepExecution {
  id: string;
  // The subject the step ran on: exactly one is set.
  signal_id: string | null;
  prospect_order_id: string | null;
  schedule_run_id?: string | null;
  workflow_id: string | null;
  workflow_name: string | null;
  step_id: string | null;
  parent_step_id: string | null;
  step_type: string;
  // For a person: the agent's name, the list, the channel.
  step_label: string;
  // The step's config, so the page can say what approving will spend.
  step_config: Record<string, unknown>;
  status: StepExecutionStatus;
  result: Record<string, unknown>;
  error: string | null;
  // Who approved or rejected a step that required approval, when, and why.
  reviewed_by_user_id: string | null;
  reviewed_at: string | null;
  review_note: string | null;
  created_at: string;
  updated_at: string;
}

export type StepExecutionStatus =
  | "pending"
  | "success"
  | "failed"
  | "skipped"
  // The step requires a person's approval and has not run.
  | "awaiting_approval"
  // Declined; it can still be approved later.
  | "rejected";

export interface ProspectOrderWorkflow {
  id: string;
  name: string;
  active: boolean;
  // Step types in tree order (under `origin_step_id`, when set).
  step_types: string[];
  // The step that made the order: only the steps under it run on the order.
  origin_step_id: string | null;
  executions: StepExecution[];
}

export interface ProspectOrderWorkflows {
  tenant_slug: string | null;
  // False when the order was placed by an org other than the tenant's owner:
  // the owner's workflows (and their spend) never run on it.
  automated: boolean;
  workflows: ProspectOrderWorkflow[];
}

export interface ProspectOrderRunResult {
  workflows: { id: string; name: string }[];
}

export interface AgentRunPlan {
  tenant_id: string;
  // Only the tenant's owning org runs agents for it (they bill its Claude plan).
  owned: boolean;
  owner_name: string | null;
  // The owner has a Claude session token.
  credential_ok: boolean;
  // The order workflow whose agent runs; null = the built-in agent alone.
  workflow: {
    id: string;
    name: string;
    step_types: string[];
    // Its agent step waits for approval; "Run agent" approves it.
    requires_approval: boolean;
  } | null;
  // What runs: the workflow's agent, or the built-in prospecting agent.
  agent: { id: string; name: string; model: string } | null;
  models: AgentModelCatalog;
}

export interface ProspectOrderAgentRunCreate extends ProspectOrderCreate {
  model?: string | null;
  // An existing collection to deliver into; else a new one named `collection_name`.
  collection_id?: string | null;
  collection_name?: string | null;
}

export interface ProspectOrderAgentRunResult {
  order: ProspectOrderDetail;
  collection_id: string;
  workflow: { id: string; name: string } | null;
  agent_run_id: string | null;
  needs_approval: boolean;
}

export interface WorkflowTriggerType {
  slug: string;
  label: string;
}

export interface WorkflowDataType {
  slug: string;
  label: string;
  // One organization's own records (Notion).
  private: boolean;
  // Whether a workflow can match its records by rules, without the LLM.
  supports_rules: boolean;
  // The data type's own settings, stored as the trigger config's `source`.
  config_fields: FieldSpec[];
  // Only when asked with a tenant: choices for its `choice_list` fields, and
  // why the tenant's workflows would never see a record.
  field_options: FieldOptions | null;
  unavailable_reason: string | null;
}

// What a workflow step runs on: the trigger's subject at the root, or the
// prospect order a "Create prospect order" step made, under that step.
export type WorkflowSubjectKind =
  | typeof SIGNAL_TRIGGER
  | typeof PROSPECT_ORDER_TRIGGER
  | typeof SCHEDULE_TRIGGER;

export interface WorkflowStepType {
  slug: string;
  label: string;
  // The kinds of subject the step can run on.
  subject_kinds: WorkflowSubjectKind[];
  // Set when the step makes a new subject for the steps chained under it.
  child_subject_kind: WorkflowSubjectKind | null;
  // Whether steps can be chained under this one.
  supports_children: boolean;
}

// What an agent hands back; decides what steps chained under it can branch on.
export type AgentContractKey = "text" | "structured" | "prospect_list" | "signal_brief" | "contact_list";

// Where an agent's tools come from. `hive` is implicit on every agent and never
// stored; the others are declared on the definition and opted in per step.
export const HIVE_DATA_SOURCE = "hive";

// "number" is money (2 dp). "string_list" picks from `options`; "choice_list"
// from the per-tenant `field_options[name]`; "conditions" is a list of
// `PropertyCondition`s whose ops are the `options`.
export type FieldSpecType =
  | "number"
  | "integer"
  | "string"
  | "boolean"
  | "string_list"
  | "choice_list"
  | "conditions";

// One setting a data source (per step) or a signal data type (per trigger)
// declares; the editor renders them without knowing the source.
export interface FieldSpec {
  name: string;
  label: string;
  type: FieldSpecType;
  required: boolean;
  minimum?: number | null;
  maximum?: number | null;
  default?: unknown;
  help_text?: string;
  // Choices for a string_list field; the operators for a conditions field.
  options?: string[] | null;
  // Display names for `options`.
  option_labels?: Record<string, string> | null;
}

export interface FieldOption {
  value: string;
  label: string;
}

// Per-tenant choices for `choice_list` fields, by field name.
export type FieldOptions = Record<string, FieldOption[]>;

// One row of a `conditions` field; ops like "is_empty" carry no value.
export interface PropertyCondition {
  property: string;
  op: string;
  value: string;
}

// One per-step setting a data source asks for (e.g. Clay's spend cap).
export type DataSourceStepField = FieldSpec;

export interface DataSourceOption {
  key: string;
  label: string;
  description: string;
  implicit: boolean;
  // On for every run of an agent whose definition lists it; a step neither
  // opts in nor sets anything (GetLeads, a free partner).
  always_on: boolean;
  spends_money: boolean;
  step_fields: DataSourceStepField[];
  // Only meaningful when asked with a tenant: whether its organization has it set up.
  configured: boolean;
  unavailable_reason?: string | null;
  // Only when asked with a tenant: choices for its `choice_list` step fields.
  field_options?: FieldOptions | null;
}

export type AgentOutputFieldType = "string" | "number" | "integer" | "boolean" | "enum";

// One field of a structured agent's result.
export interface AgentOutputField {
  name: string;
  type: AgentOutputFieldType;
  description: string;
  required: boolean;
  enum_values: string[];
}

export interface AgentDefinition {
  id: string;
  // null = the global library.
  tenant_id: string | null;
  source_definition_id: string | null;
  // "prospecting" | "signal_research" | "contact_research" for the built-in agents.
  builtin_key: string | null;
  name: string;
  description: string | null;
  instructions: string;
  // A built-in agent running its code default instructions.
  is_default_instructions: boolean;
  model: string | null;
  max_turns: number;
  hive_tools: string[];
  // Non-implicit data sources steps running this agent may opt in to.
  data_sources: string[];
  web_access: boolean;
  output_contract: AgentContractKey;
  output_fields: AgentOutputField[];
  enabled: boolean;
  workflow_count: number;
  created_at: string;
  updated_at: string;
}

export interface AgentDefinitionCreate {
  tenant_id?: string | null;
  name: string;
  description?: string | null;
  instructions: string;
  model?: string | null;
  max_turns: number;
  hive_tools: string[];
  data_sources: string[];
  web_access: boolean;
  output_contract: AgentContractKey;
  output_fields: AgentOutputField[];
  enabled?: boolean;
}

export type AgentDefinitionUpdate = Partial<Omit<AgentDefinitionCreate, "tenant_id">> & {
  reset_instructions?: boolean;
};

// Reviewer feedback on an agent's work (developer view): the raw ledger and
// the rules the distiller made of it.
export interface AgentFeedbackLesson {
  polarity: "positive" | "negative" | "mixed";
  kind: "evaluation" | "instruction";
  category: string;
  rule: string;
  specifics: string;
}

export interface AgentFeedbackRow {
  id: string;
  created_at: string;
  source_type: "step_review" | "brief_review" | "prospect_entry" | "contact_entry";
  decision: "approved" | "rejected" | "accepted" | "disqualified" | "retracted";
  timing: "pre_run" | "post_run";
  note: string | null;
  reviewer_name: string | null;
  agent_run_id: string | null;
  workflow_id: string | null;
  workflow_name: string | null;
  context: Record<string, unknown>;
  lesson: { lessons?: AgentFeedbackLesson[]; trivial?: boolean; verbatim?: boolean } | null;
  distill_error: string | null;
  digest_version: number | null;
}

export interface AgentFeedbackRule {
  rule_key: string;
  rule: string;
  polarity: "positive" | "negative" | "mixed";
  kind: "evaluation" | "instruction";
  category: string;
  feedback_ids: string[];
  workflow_ids: string[];
  support_count: number;
  retracted_count?: number;
  last_seen_at: string;
  example_quote: string | null;
}

export interface AgentFeedbackDigest {
  id: string;
  version: number;
  created_at: string;
  rules: AgentFeedbackRule[];
  other: Record<string, { rule: string; polarity: string; feedback_ids: string[] }[]>;
  rendered: string;
  lesson_count: number;
  decision_count: number;
  model: string | null;
}

export interface AgentFeedbackPage {
  tenant_id: string;
  root_definition_id: string;
  digest: AgentFeedbackDigest | null;
  scope: {
    dirty_since: string | null;
    building_until: string | null;
    last_built_at: string | null;
    last_version: number;
  } | null;
  feedback: AgentFeedbackRow[];
  total: number;
}

export interface AgentToolOption {
  name: string;
  description: string;
}

export interface AgentContractOption {
  key: AgentContractKey;
  label: string;
  description: string;
  // The kinds of subject (signal, prospect order) the contract works on.
  subject_kinds: string[];
  requires_step_instructions: boolean;
}

export interface AgentModelOption {
  id: string;
  display_name: string;
  created_at: string | null;
  // Its family ("opus", "sonnet", ...) and that family's one-line hint, from
  // the backend's model families; null for a model outside them.
  family: string | null;
  hint: string | null;
}

export interface AgentModelCatalog {
  // "anthropic" = listed by Anthropic; "fallback" = a standard list when it can't be asked.
  source: "anthropic" | "fallback";
  // What a blank model runs on.
  default_model: string;
  // Newest first.
  models: AgentModelOption[];
}

export interface SlackChannelOption {
  id: string;
  name: string;
  is_private: boolean;
}

// Channels a "Send to Slack" step can post to, in the tenant's org's workspace.
export interface SlackChannels {
  connected: boolean;
  team_name: string | null;
  needs_reauth: boolean;
  channels: SlackChannelOption[];
}

// Where the workflow editor draws a step.
export interface WorkflowPosition {
  x: number;
  y: number;
}

export interface WorkflowStep {
  id: string;
  workflow_id: string;
  step_type: string;
  config: Record<string, unknown>;
  enabled: boolean;
  // null = a root step, fired by the trigger.
  parent_step_id: string | null;
  // When this step fires: for a root step the trigger's gate
  // ({ min_rating }), for a chained one its parent's outcome, e.g.
  // { verdicts: ["verified", "qualified"] } under a research agent.
  condition: Record<string, unknown>;
  // The step waits for a person's approval instead of firing when its
  // condition is met.
  requires_approval: boolean;
  // null = auto-layout.
  position: WorkflowPosition | null;
  created_at: string;
  updated_at: string;
}

export interface WorkflowStepInput {
  // Echo an existing step's id to keep it (and its history) across an edit; a
  // new step may carry a client-generated id so children can name it.
  id?: string;
  step_type: string;
  config: Record<string, unknown>;
  enabled?: boolean;
  parent_step_id: string | null;
  condition?: Record<string, unknown>;
  requires_approval?: boolean;
  position?: WorkflowPosition | null;
}

export const SIGNAL_TRIGGER = "signal";
export const PROSPECT_ORDER_TRIGGER = "prospect_order";
export const SCHEDULE_TRIGGER = "schedule";

// The `signal` trigger's config: which data stream to watch and what the
// evaluator looks for in each record, plus the data type's own settings
// (`source`, keyed by its `config_fields`; absent for public data types).
// The `prospect_order` trigger has none.
// `match_mode: "rules"` (only for data types that `supports_rules`) skips the
// LLM: the source's settings decide alone and the prompt is unused.
export type SignalMatchMode = "llm" | "rules";

export interface SignalTriggerConfig {
  data_type: string;
  prompt: string;
  match_mode?: SignalMatchMode;
  source?: Record<string, unknown>;
}

// The `schedule` trigger's config: a calendar preset in a time zone. Hourly
// takes `minute`; daily `time` (HH:MM); weekly `time` + `weekdays` (0 = Monday).
export type ScheduleFrequency = "hourly" | "daily" | "weekly";

export interface ScheduleTriggerConfig {
  frequency: ScheduleFrequency;
  timezone: string;
  minute?: number;
  time?: string;
  weekdays?: number[];
}

export type WorkflowTriggerConfig =
  | SignalTriggerConfig
  | ScheduleTriggerConfig
  | Record<string, never>;

export interface Workflow {
  id: string;
  tenant_id: string;
  name: string;
  trigger_type: string;
  trigger_config: Record<string, unknown>;
  trigger_hash: string | null;
  active: boolean;
  // A signal workflow evaluates only records queued since then.
  active_since: string | null;
  // A schedule workflow's next slot (null while paused) and the last it fired.
  next_fire_at?: string | null;
  last_fired_at?: string | null;
  source: string;
  created_at: string;
  updated_at: string;
  // Every step, flat; `parent_step_id` gives the tree.
  steps: WorkflowStep[];
}

export function scheduleTriggerConfigOf(
  w: Pick<Workflow, "trigger_config">,
): ScheduleTriggerConfig {
  const c = w.trigger_config ?? {};
  const frequency: ScheduleFrequency =
    c.frequency === "hourly" || c.frequency === "weekly" ? c.frequency : "daily";
  const timezone = typeof c.timezone === "string" && c.timezone ? c.timezone : "Europe/Oslo";
  if (frequency === "hourly") {
    return { frequency, timezone, minute: typeof c.minute === "number" ? c.minute : 0 };
  }
  const time = typeof c.time === "string" ? c.time : "08:00";
  if (frequency === "daily") return { frequency, timezone, time };
  const weekdays = Array.isArray(c.weekdays)
    ? c.weekdays.filter((d): d is number => typeof d === "number")
    : [0, 1, 2, 3, 4];
  return { frequency, timezone, time, weekdays };
}

// One run of a schedule workflow and what its steps did.
export interface WorkflowScheduleRun {
  id: string;
  workflow_id: string;
  scheduled_for: string;
  // "scheduled" (a slot) | "manual" (Run now)
  kind: "scheduled" | "manual";
  triggered_by_user_id: string | null;
  status: "fired" | "skipped";
  // "previous_run_active": the last run was still working at the slot.
  skip_reason: string | null;
  created_at: string;
  executions: StepExecution[];
}

export function signalTriggerConfigOf(w: Pick<Workflow, "trigger_config">): SignalTriggerConfig {
  const c = w.trigger_config ?? {};
  const source = c.source;
  return {
    data_type: typeof c.data_type === "string" ? c.data_type : "",
    prompt: typeof c.prompt === "string" ? c.prompt : "",
    ...(c.match_mode === "rules" ? { match_mode: "rules" as const } : {}),
    ...(typeof source === "object" && source !== null && !Array.isArray(source)
      ? { source: source as Record<string, unknown> }
      : {}),
  };
}

export interface WorkflowCreate {
  tenant_id: string;
  name: string;
  trigger_type: string;
  trigger_config: WorkflowTriggerConfig;
  active?: boolean;
  steps?: WorkflowStepInput[];
}

export interface WorkflowUpdate {
  name?: string;
  // Replaces the whole trigger config; a signal's data_type cannot change.
  trigger_config?: WorkflowTriggerConfig;
  active?: boolean;
  // Present (even []) replaces every step; omit to leave them untouched.
  steps?: WorkflowStepInput[];
}

export interface Signal {
  id: string;
  tenant_id: string;
  workflow_id: string;
  workflow_name: string | null;
  data_type: string;
  record_id: string;
  master_company_id: string | null;
  master_company_name: string | null;
  matched: boolean;
  rating: number | null;
  reasoning: string | null;
  model: string | null;
  llm_call_log_id: string | null;
  created_at: string;
  source_date: string | null;
  // ISO alpha-2 the signal comes from: the record's or its company's, then
  // what research reported; null when none is known.
  country: string | null;
  // Latest research run and brief, when the signal has been researched.
  research_status: string | null;
  research_verdict: SignalBriefVerdict | null;
  research_confidence: number | null;
  // The buyer the latest brief names, even when it is not in the database.
  research_buyer_name: string | null;
  // A workflow step is parked until someone continues it on this signal.
  awaiting_approval: boolean;
  // For records the app has no page of its own for (a Notion page): what to
  // call it and where it lives.
  record_title: string | null;
  record_url: string | null;
  // The tenant's prospect this signal is a hit on, if its steps reached a company.
  prospect_id: string | null;
  prospect_review_state: ProspectReviewState | null;
  prospect_hit_count: number | null;
  // Runs still in flight on this signal, any agent.
  active_agent_runs: ActiveAgentRun[];
}

export type SignalReviewState = "accepted" | "pending" | "disqualified";

export interface SignalContinueResult {
  signal_id: string;
  approved: number;
  skipped: string[];
}

export interface SignalContinueResponse {
  results: SignalContinueResult[];
}

export type SignalReviewAction = "accept" | "disqualify" | "reset";

export interface SignalDashboardParams {
  tenant_id: string;
  workflow_id?: string;
  since?: string;
}

export interface SignalDashboard {
  evaluated: number;
  matched: number;
  researched: number;
  by_verdict: Record<SignalBriefVerdict, number>;
  qualified_or_verified: number;
  // Per day, or per week over long periods; buckets without signals are left out.
  timeline_bucket: "day" | "week";
  timeline: SignalDashboardBucket[];
}

export interface SignalDashboardBucket {
  start: string;
  matched: number;
  qualified_or_verified: number;
}

export interface SignalCount {
  total: number;
}

export interface SignalLocation {
  signal: Signal;
  // Position in the list under the requested filters, newest first; null when
  // the signal doesn't match them.
  index: number | null;
}

// ---- The tenant's prospects: one row per company, every time it was prospected ----

export type ProspectReviewState = "accepted" | "pending" | "disqualified";
export type ProspectQueue = "inbox" | "worklist" | "done";
export type ProspectHitKind = "signal" | "agent" | "workflow" | "manual" | "mcp" | "bulk_filter";
export type ProspectSort =
  | "last_hit"
  | "first_hit"
  | "hits"
  | "company"
  | "done"
  // With `collection_id` only: the entry's date added, its contacts, a label.
  | "added"
  | "contacts"
  | `label:${string}`;

export interface ProspectFilters {
  tenant_id: string;
  queue?: ProspectQueue;
  review_state?: ProspectReviewState[];
  // Companies with a hit of any of these kinds ("manual" stands for every kind a person caused).
  kinds?: ProspectHitKind[];
  agent_definition_id?: string;
  workflow_id?: string;
  prospect_order_id?: string;
  // Companies this agent run found.
  agent_run_id?: string;
  collection_id?: string;
  master_company_id?: string;
  min_hits?: number;
  // Hits since the last decision.
  new_hits?: boolean;
  awaiting_approval?: boolean;
  // ISO alpha-2; NO_COUNTRY for none known.
  country?: string[];
  // Last hit at or after (ISO).
  since?: string;
  q?: string;
  // true: only snoozed; otherwise snoozed ones are left out.
  snoozed?: boolean;
  // With `collection_id` only: the entry's state there, and `key:value` labels it carries.
  entry_state?: ProspectReviewState[];
  label?: string[];
  sort?: ProspectSort;
  dir?: "asc" | "desc";
}

export function prospectQuery(params: ProspectFilters): Query {
  return {
    tenant_id: params.tenant_id,
    queue: params.queue,
    review_state: params.review_state,
    kinds: params.kinds,
    agent_definition_id: params.agent_definition_id,
    workflow_id: params.workflow_id,
    prospect_order_id: params.prospect_order_id,
    agent_run_id: params.agent_run_id,
    collection_id: params.collection_id,
    master_company_id: params.master_company_id,
    min_hits: params.min_hits,
    new_hits: params.new_hits,
    awaiting_approval: params.awaiting_approval,
    country: params.country,
    since: params.since,
    q: params.q,
    snoozed: params.snoozed,
    entry_state: params.entry_state,
    label: params.label,
    sort: params.sort,
    dir: params.dir,
  };
}

/** A prospecting agent run, for the "Agent run" filter. */
export interface ProspectRunOption {
  id: string;
  /** The order it ran on; null for a run without one. */
  subject: string | null;
  agent_name: string | null;
  status: string;
  created_at: string;
  prospect_order_id: string | null;
  collection_id: string | null;
  /** Companies it found. */
  prospect_count: number;
}

/** One string per distinct filter set, for query keys. */
export function prospectFilterKey(params: ProspectFilters): string {
  return JSON.stringify(prospectQuery(params));
}

/** What prospected the company, with how often: a signal workflow, an agent, people. */
export interface ProspectSource {
  kind: "signal" | "agent" | "manual";
  agent_definition_id: string | null;
  agent_name: string | null;
  workflow_id: string | null;
  workflow_name: string | null;
  count: number;
}

export interface ProspectLastHit {
  kind: ProspectHitKind;
  occurred_at: string;
  headline: string | null;
  signal_id: string | null;
  agent_run_id: string | null;
  prospect_order_id: string | null;
  agent_name: string | null;
}

/** The company's entry on the collection the list was asked for (`collection_id`). */
export interface CollectionEntrySummary {
  entry_id: string;
  // The entry's own state there; the prospect's `review_state` rolls up every collection.
  review_state: ProspectReviewState;
  labels: Record<string, string>;
  inclusion_reason: string | null;
  disqualification_reason: string | null;
  disqualified_at: string | null;
  disqualified_by_user_id: string | null;
  disqualified_by_agent_run_id: string | null;
  added_by_user_id: string | null;
  added_by_agent_run_id: string | null;
  added_with_method: string;
  created_at: string;
  contact_count: number;
  missing_required_properties: string[];
}

export interface Prospect {
  id: string;
  tenant_id: string;
  master_company_id: string | null;
  company_name: string | null;
  company_country: string | null;
  company_domain: string | null;
  // False for a company the database does not hold (an unresolved entry).
  resolved: boolean;
  // Rolled up from the company's entries on the tenant's lists.
  review_state: ProspectReviewState;
  decision: ProspectReviewState | null;
  decided_at: string | null;
  decided_by_user_id: string | null;
  snoozed_until: string | null;
  done_at: string | null;
  // A hit after it was finished put it back in the inbox.
  reopened_at: string | null;
  hit_count: number;
  signal_hit_count: number;
  agent_hit_count: number;
  manual_hit_count: number;
  // Hits since a person decided or finished it.
  hits_since_decision: number;
  first_hit_at: string;
  last_hit_at: string;
  last_hit_kind: ProspectHitKind;
  last_hit: ProspectLastHit | null;
  // A workflow step is parked until someone approves it on one of its signals.
  awaiting_approval: boolean;
  // Runs still in flight on one of its signals.
  active_agent_runs: ActiveAgentRun[];
  collection_count: number;
  sources: ProspectSource[];
  // Only when listed with `collection_id`.
  entry: CollectionEntrySummary | null;
  created_at: string;
  updated_at: string;
}

export interface ProspectHitSignal {
  id: string;
  workflow_id: string | null;
  workflow_name: string | null;
  data_type: string;
  record_id: string;
  rating: number | null;
  verdict: SignalBriefVerdict | null;
  confidence: number | null;
  headline: string | null;
  record_title: string | null;
  record_url: string | null;
}

export interface ProspectHitRun {
  id: string;
  status: string;
  agent_type: string;
  agent_name: string | null;
  agent_definition_id: string | null;
}

export interface ProspectHitOrder {
  id: string;
  title: string;
  organization_id: string;
}

export interface ProspectHitList {
  id: string;
  name: string;
  collection_type: ProspectListType;
  entry_id: string | null;
  entry_review_state: ProspectReviewState | null;
}

export interface ProspectHitActor {
  id: string;
  display_name: string | null;
  picture_url: string | null;
}

export interface ProspectHit {
  id: string;
  kind: ProspectHitKind;
  source_key: string;
  occurred_at: string;
  headline: string | null;
  step_id: string | null;
  // The signal is gone (a re-run) when `signal_id` is set and `signal` is not.
  signal_id: string | null;
  signal: ProspectHitSignal | null;
  agent_run: ProspectHitRun | null;
  // Only for members of the order's organization.
  order: ProspectHitOrder | null;
  list: ProspectHitList | null;
  actor: ProspectHitActor | null;
}

/** The company's entry on one of the tenant's lists. */
export interface ProspectListMembership {
  collection_id: string;
  collection_name: string;
  collection_type: ProspectListType;
  archived: boolean;
  entry_id: string;
  review_state: ProspectReviewState;
  // An unresolved company's snapshot, for linking it to a company.
  company_org_number: string | null;
  company_website: string | null;
  added_with_method: string;
  added_by_user_id: string | null;
  added_by_agent_run_id: string | null;
  inclusion_reason: string | null;
  disqualification_reason: string | null;
  disqualified_at: string | null;
  disqualified_by_user_id: string | null;
  disqualified_by_agent_run_id: string | null;
  labels: Record<string, string>;
  // Required company properties the company is missing (the tenant's settings).
  missing_required_properties: string[];
  created_at: string;
}

/** What the database already holds on a prospect's company. */
export interface ProspectCompanyFacts {
  website: string | null;
  linkedin_url: string | null;
  org_number: string | null;
  org_form: string | null;
  employee_count: number | null;
  ai_summary: string | null;
  ai_industries: string[];
  // Its customers' industries, and who it sells to (B2B, B2C, ...).
  ai_served_verticals: string[];
  ai_customer_type: string[];
  technology_count: number;
  // Latest filing in `currency` (the tenant's); as filed in `reported_*`.
  currency: string;
  revenue: number | null;
  operating_result: number | null;
  reported_revenue: number | null;
  reported_operating_result: number | null;
  reported_currency: string | null;
  fiscal_year: number | null;
}

export interface ProspectDetail extends Prospect {
  // Null for a company the database does not hold.
  company: ProspectCompanyFacts | null;
  // Newest first.
  hits: ProspectHit[];
  collections: ProspectListMembership[];
  // Steps waiting for a person (or declined) on the prospect's signals.
  executions: StepExecution[];
  contacts: ProspectListContactEntry[];
}

export interface ProspectCount {
  total: number;
}

export interface ProspectQueueCounts {
  inbox: number;
  worklist: number;
  snoozed: number;
  done: number;
  done_in_period: number;
  reopened: number;
  new_hits_since_decision: number;
}

export interface ProspectReviewCounts {
  pending: number;
  accepted: number;
  disqualified: number;
}

export interface ProspectKindCounts {
  kind: "signal" | "agent" | "manual";
  hits: number;
  prospects: number;
}

export interface ProspectDashboardBucket {
  start: string;
  signal: number;
  agent: number;
  manual: number;
}

export interface ProspectDashboardTop {
  id: string;
  company_name: string | null;
  company_country: string | null;
  master_company_id: string | null;
  hit_count: number;
  review_state: ProspectReviewState;
  done_at: string | null;
  last_hit_at: string;
}

export interface ProspectDashboard {
  queues: ProspectQueueCounts;
  review: ProspectReviewCounts;
  hits_in_period: number;
  prospects_hit_in_period: number;
  by_kind: ProspectKindCounts[];
  timeline_bucket: "day" | "week";
  timeline: ProspectDashboardBucket[];
  top: ProspectDashboardTop[];
}

/** In the order they are done: Claude runs the onboarding agent that fills in the next steps. */
export type OnboardingStepKey =
  | "claude"
  | "profile"
  | "logo"
  | "signal_workflow"
  | "agent_workflow"
  | "crm"
  | "first_decision";

export interface OnboardingStep {
  key: OnboardingStepKey;
  done: boolean;
}

export interface OrgDashboardTenant {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  /** The org owns the tenant; a shared tenant has no onboarding and no agent work here. */
  owned: boolean;
  inbox: number;
  worklist: number;
  done_in_period: number;
  hits_in_period: number;
  prospects_hit_in_period: number;
  /** Hits per UTC day, the last 14 days up to today. */
  daily_hits_14d: number[];
  active_workflows: number;
  onboarding: OnboardingStep[] | null;
}

export interface OrgAgentStats {
  agent_type: string;
  runs: number;
  completed: number;
  failed: number;
  active: number;
  research_seconds: number;
  companies_found: number;
}

export interface OrgAgentRun {
  id: string;
  agent_type: string;
  agent_name: string | null;
  status: string;
  tenant_id: string;
  subject: string | null;
  prospect_order_id: string | null;
  signal_id: string | null;
  // The schedule workflow a scheduled run belongs to.
  schedule_workflow_id?: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

export interface OrgLiveRun extends OrgAgentRun {
  activity_kind: string | null;
  activity_tool: string | null;
  activity_detail: string | null;
  activity_at: string | null;
}

export interface OrgRecentRun extends OrgAgentRun {
  companies_found: number;
  summary: string | null;
}

export interface OrgOpenOrder {
  id: string;
  title: string;
  status: string;
  tenant_id: string;
  requested_count: number | null;
  delivered: number;
  deadline: string | null;
  agent_running: boolean;
  created_at: string;
}

export interface OrgDashboard {
  tenants: OrgDashboardTenant[];
  integrations: { claude: boolean; notion: boolean; slack: boolean; contact_data: boolean };
  by_kind: ProspectKindCounts[];
  timeline_bucket: "day" | "week";
  timeline: ProspectDashboardBucket[];
  /** Reviewer decisions on agent work in the period. */
  decisions: { positive: number; negative: number };
  agents: OrgAgentStats[];
  live: OrgLiveRun[];
  recent: OrgRecentRun[];
  orders: OrgOpenOrder[];
}

export interface ProspectLocation {
  prospect: Prospect;
  index: number | null;
}

export interface ProspectReviewRequest {
  prospect_ids: string[];
  action: SignalReviewAction;
  reason?: string | null;
  snooze_until?: string | null;
}

export interface ProspectReviewResult {
  prospect_id: string;
  review_state: ProspectReviewState | null;
  entries_updated: number;
  approved: number;
  rejected: number;
  skipped: string | null;
}

export interface ProspectReviewResponse {
  results: ProspectReviewResult[];
}

export interface ProspectContinueResult {
  prospect_id: string;
  approved: number;
  skipped: string[];
}

export interface ProspectContinueResponse {
  results: ProspectContinueResult[];
}

export interface ProspectUpdateResponse {
  updated: string[];
  missing: string[];
  // Found, but not the kind the action applies to (snooze: not on the worklist).
  skipped: string[];
}

export type SignalBriefVerdict =
  | "verified"
  | "qualified"
  | "disqualified"
  | "inconclusive"
  // Another signal of the workflow covers the same event (`duplicate_of_signal_id`).
  | "duplicate";
export type SignalBriefSensitivity = "low" | "medium" | "high";
// A person's decision on the brief; every brief starts as "draft".

export interface SignalBriefExecutiveSummary {
  // Why the signal matters to the workflow; absent on briefs filed before it existed.
  why_selected?: string | null;
  facts: string[];
  // The verdict and the deciding reason, in one sentence.
  outcome: string;
}

export interface SignalBriefAssessmentAnswer {
  answer: "yes" | "no" | "unclear";
  reason: string;
}

// A stored file as its owner's endpoint returns it. `download_url` is a
// same-origin API path that streams the file (the buckets are private).
export interface Attachment {
  id: string;
  filename: string;
  content_type: string;
  size_bytes: number | null;
  title: string | null;
  description: string | null;
  source_url: string | null;
  created_at: string;
  download_url: string | null;
}

export interface SignalBriefSource {
  url: string;
  title?: string;
  publisher?: string;
  published_at?: string;
  note?: string;
}

export interface SignalBriefCompany {
  master_company_id?: string;
  name: string;
  role?: string;
  reason?: string;
}

// A person the research names, acting for an organisation involved.
export interface SignalBriefPerson {
  name: string;
  title?: string;
  organisation?: string;
  master_company_id?: string;
  involvement?: string;
  source_url?: string;
}

export interface SignalBriefBuyerDetails {
  // Its part in the event, e.g. "stadium owner".
  role?: string;
  certainty?: "confirmed" | "presumed";
  country?: string;
  org_number?: string;
  website?: string;
}

export interface SignalBrief {
  id: string;
  signal_id: string;
  agent_run_id: string | null;
  verdict: SignalBriefVerdict;
  // With verdict "duplicate": the signal whose research covers the same event.
  duplicate_of_signal_id: string | null;
  confidence: number | null;
  headline: string;
  summary: string;
  sources: SignalBriefSource[];
  related_companies: SignalBriefCompany[];
  // Structured findings; null where the agent could not establish them.
  trigger_type: string | null;
  event_date: string | null;
  trigger_date: string | null;
  buyer_master_company_id: string | null;
  buyer_name: string | null;
  // What does not belong in the name; empty on briefs filed before it existed.
  buyer_details: SignalBriefBuyerDetails;
  // The country research reported the signal comes from (ISO alpha-2); null on
  // duplicates, fallback briefs and briefs filed before it existed.
  country: string | null;
  sensitivity: SignalBriefSensitivity | null;
  // ISO date; no outreach before it.
  hold_until: string | null;
  recommended_roles: string[];
  people: SignalBriefPerson[];
  call_brief: string | null;
  // Null on duplicates, fallback briefs and briefs filed before they existed.
  executive_summary: SignalBriefExecutiveSummary | null;
  // The questions the verdict follows from, keyed in asking order.
  assessment: Record<string, SignalBriefAssessmentAnswer> | null;
  attachments: Attachment[];
  created_at: string;
  updated_at: string;
}

export interface SignalProspectList {
  id: string;
  name: string;
  holding: boolean;
  collection_type: ProspectListType;
  // The entry a step made for this signal on the list, and its review state.
  entry_id: string | null;
  review_state: SignalReviewState | null;
}

export interface SignalResearch {
  signal_id: string;
  // The tenant's prospect the signal is a hit on; null when it reached no company.
  prospect_id: string | null;
  can_research: boolean;
  unavailable_reason: string | null;
  // Newest first.
  runs: AgentRun[];
  briefs: SignalBrief[];
  // People at the signal's buyer on the workflow's holding list, newest first.
  contacts: ProspectListContactEntry[];
  // The workflow's holding list (its agents' finds) first, then every list a
  // step copied to.
  collections: SignalProspectList[];
  // Every workflow step that fired (or is waiting to) on the signal, oldest first.
  executions: StepExecution[];
}

export interface ProspectMembership {
  collection_id: string;
  collection_name: string;
  tenant_id: string;
  tenant_name: string;
  tenant_slug: string;
  entry_id: string;
  entry_created_at: string;
  signal_trigger_count: number;
  last_signal_at: string | null;
  signal_weekly_counts: number[];
}

export interface AgentSkill {
  id: string;
  tenant_id: string | null;
  slug: string;
  title: string;
  description: string;
  body: string;
  enabled: boolean;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface AgentSkillCreate {
  tenant_id: string | null;
  slug: string;
  title: string;
  description: string;
  body: string;
  enabled?: boolean;
}

export interface AgentSkillUpdate {
  slug?: string;
  title?: string;
  description?: string;
  body?: string;
  enabled?: boolean;
}

// Micro app types

export interface MicroApp {
  id: string;
  tenant_id: string | null;
  slug: string;
  title: string;
  description: string;
  icon: string | null;
  enabled: boolean;
  access_mode: MicroAppAccessMode;
  show_in_nav: boolean;
  nav_order: number;
  current_version_id: string | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export type MicroAppAccessMode = "everyone" | "restricted";

/** An app as its writers see it (`/micro-apps/manage`). */
export interface MicroAppManaged extends MicroApp {
  tv_share_code: string | null;
}

/** An app's TV share; every field is null when it is not shared. */
export interface MicroAppTvShare {
  code: string | null;
  url: string | null;
  shared_by_user_id: string | null;
  shared_at: string | null;
}

export interface MicroAppGrant {
  group_id: string;
  organization_id: string;
  name: string;
  description: string;
}

export interface UserGroup {
  id: string;
  organization_id: string;
  name: string;
  description: string;
  member_count: number;
  created_at: string;
  updated_at: string;
}

export interface UserGroupMember {
  id: string;
  user_id: string;
  user_email: string;
  user_display_name: string;
  created_at: string;
}

// A list page's URL filter params saved under a name. `private` views keep the
// context they were saved in (tenant / org / neither) and list only there.
export type SavedViewVisibility = "private" | "tenant" | "organization";

export type SavedViewParams = Record<string, string | string[]>;

export interface SavedView {
  id: string;
  page_key: string;
  name: string;
  params: SavedViewParams;
  visibility: SavedViewVisibility;
  tenant_id: string | null;
  organization_id: string | null;
  owner_user_id: string;
  owner_display_name: string | null;
  owner_email: string;
  is_owner: boolean;
  can_delete: boolean;
  created_at: string;
  updated_at: string;
}

export interface SavedViewCreate {
  page_key: string;
  name: string;
  params: SavedViewParams;
  visibility: SavedViewVisibility;
  tenant_id?: string | null;
  organization_id?: string | null;
}

// The context is fixed at creation; visibility moves within it.
export interface SavedViewUpdate {
  name?: string;
  params?: SavedViewParams;
  visibility?: SavedViewVisibility;
}

export interface MicroAppVersion {
  id: string;
  app_id: string;
  version: number;
  manifest: MicroAppManifest;
  client_code: string | null;
  server_code: string | null;
  content_hash: string;
  notes: string | null;
  published_by_user_id: string | null;
  created_at: string;
}

export interface MicroAppPublishBody {
  manifest: Record<string, unknown>;
  client_code: string | null;
  server_code: string | null;
  notes: string | null;
  publish: boolean;
}

export interface MicroAppPublishResponse {
  app: MicroApp;
  version: MicroAppVersion;
  warnings: string[];
}

export interface MicroAppCreate {
  tenant_id: string | null;
  slug: string;
  title: string;
  description: string;
  access_mode: MicroAppAccessMode;
  initial_version?: MicroAppPublishBody;
}

export interface MicroAppCreateResponse extends MicroApp {
  version: MicroAppVersion | null;
  warnings: string[];
}

export interface MicroAppUpdate {
  title?: string;
  description?: string;
  icon?: string | null;
  enabled?: boolean;
  access_mode?: MicroAppAccessMode;
  show_in_nav?: boolean;
  nav_order?: number;
  current_version_id?: string;
}

export type MicroAppViewType = "table" | "stat" | "line" | "bar";

export interface MicroAppView {
  type: MicroAppViewType;
  title: string;
  query: string;
  columns?: string[] | null;
  column?: string | null;
  agg: "sum" | "count" | "avg" | "min" | "max" | "first";
  unit?: string | null;
  x?: string | null;
  y?: string[] | null;
  width: "full" | "half" | "third";
}

export interface MicroAppManifest {
  manifest_version: number;
  refresh_seconds: number;
  queries: Record<string, { dataset: string; params: Record<string, unknown> }>;
  views: MicroAppView[];
}

/** A resolved query: either rows, or the reason this viewer could not read it. */
export interface MicroAppQueryResult {
  columns?: string[];
  rows?: unknown[][];
  row_count?: number;
  error?: string;
  status?: number;
}

export interface MicroAppRender {
  app: MicroApp;
  version: number;
  manifest: MicroAppManifest;
  data: Record<string, MicroAppQueryResult>;
  /** Whether this version draws itself in the sandbox rather than from `manifest.views`. */
  has_client_code: boolean;
  /**
   * Single-use URL on the sandbox host. Null on a refresh where the caller
   * already holds this version (see `knownVersion`) — keep the frame and push
   * `data` into it.
   */
  document_url: string | null;
  /** Set when server_code failed; the data below is untransformed. */
  server_error: string | null;
  fetched_at: string;
}

// Prospect List types
export interface ProspectListViewSettings {
  label_columns?: string[];
  hidden_base_columns?: string[];
  sort?: { key: string; dir: "asc" | "desc" } | null;
}

export interface UserBrief {
  user_id: string;
  display_name: string | null;
  email: string | null;
  picture_url: string | null;
}

/**
 * Where a prospect list (collection) came from, derived by the backend: an
 * order or workflow (with its id and name), an agent, MCP, or made by hand.
 */
export interface ProspectListOrigin {
  kind: "order" | "workflow" | "agent" | "mcp" | "manual";
  /** The order id or workflow id when `kind` is order/workflow. */
  id: string | null;
  /** The order title or workflow name. */
  name: string | null;
}

export interface ProspectList {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  prospect_count: number;
  signal_trigger_count: number;
  created_at: string;
  updated_at: string;
  created_by_user_id: string | null;
  created_by_display_name: string | null;
  created_by_email: string | null;
  created_by_picture_url: string | null;
  created_with_method: string | null;
  origin: ProspectListOrigin | null;
  // Runs working on the collection: its own, its order's, its workflow's.
  active_agent_runs: ActiveAgentRun[];
  collection_type: ProspectListType;
  view_settings: ProspectListViewSettings;
  archived: boolean;
  /** Non-disqualified companies missing a required property. */
  incomplete_count: number;
  collaborators: UserBrief[];
}

// "signal": a signal workflow's add-to-list step feeds it, so its entries are
// signals a reviewer uses or disqualifies.
export type ProspectListType = "standard" | "signal";

export interface ProspectListEntry {
  id: string;
  /** Null while unresolved: a company the database does not hold yet. */
  master_company_id: string | null;
  company_name: string | null;
  company_country: string | null;
  company_domain: string | null;
  company_industry: string | null;
  company_org_number: string | null;
  company_website: string | null;
  /** False until someone links the entry to a company. */
  resolved: boolean;
  added_by_user_id: string | null;
  added_by_agent_run_id: string | null;
  added_with_method: string;
  created_at: string;
  disqualified_at: string | null;
  disqualified_by_user_id: string | null;
  disqualified_by_agent_run_id: string | null;
  disqualification_reason: string | null;
  accepted_at: string | null;
  accepted_by_user_id: string | null;
  accepted_by_agent_run_id: string | null;
  inclusion_reason: string | null;
  labels: Record<string, string>;
  signal_trigger_count: number;
  // The tenant's prospect this company is: how often anything prospected it
  // (every list, signal and agent run) and the rolled-up review state.
  prospect_id: string | null;
  prospect_hit_count: number;
  // Of those, by what found it (manual = by hand, MCP or a filter).
  prospect_signal_hit_count: number;
  prospect_agent_hit_count: number;
  prospect_manual_hit_count: number;
  prospect_review_state: ProspectReviewState | null;
  /** Contacts on the same list bound to this company. */
  contact_count: number;
  /** Required company property keys this company is missing. */
  missing_required_properties: string[];
  /** Runs still in flight on one of the company's signals. */
  active_agent_runs: ActiveAgentRun[];
}

/** A company the database does not hold, added by name. */
export interface UnresolvedProspectInput {
  name: string;
  country?: string | null;
  org_number?: string | null;
  website?: string | null;
}

/** A company an unresolved list entry may be. */
export interface LinkSuggestion {
  master_company_id: string;
  name: string;
  country: string | null;
  org_number: string | null;
  domain: string | null;
  matched_on: "org_number" | "domain" | "name";
  score: number;
}

export interface ProspectListDetail extends ProspectList {
  // Empty when asked without entries (the page lists them through /prospects).
  prospects: ProspectListEntry[];
  entry_counts: { pending: number; accepted: number; disqualified: number };
  // Label key → the values its entries carry (sorted, capped per key).
  label_values: Record<string, string[]>;
}

export type CrmExportScope = "all" | "not_disqualified" | "accepted";

export interface CrmExportRequest {
  connection_id?: string;
  scope: CrmExportScope;
  include_contacts: boolean;
  create_crm_list: boolean;
  list_name?: string;
  enroll_in_sequence?: boolean;
  sequence_id?: string;
}

/**
 * Outreach-cadence state for one exported contact.
 *
 * There is deliberately no step position or replied/bounced status: HubSpot
 * only exposes those in its own reporting UI, never through an API.
 */
export interface CrmSequenceStatus {
  is_enrolled: boolean;
  sequence_id: string | null;
  sequence_name: string | null;
  enrolled_at: string | null;
  ended_at: string | null;
  enrolled_count: number | null;
  enrolled_by_email: string | null;
  last_replied_at: string | null;
  last_opened_at: string | null;
}

export interface CrmDealStatus {
  deal_id: string | null;
  deal_name: string | null;
  stage: string | null;
  pipeline_id: string | null;
  pipeline_name: string | null;
  amount: number | null;
  close_date: string | null;
  stage_entered_at: string | null;
}

export interface ProspectContactCrmStatus {
  contact_entry_id: string;
  master_contact_id: string;
  crm_contact_id: string | null;
  sequence: CrmSequenceStatus | null;
  deal: CrmDealStatus | null;
}

export interface ProspectListCrmStatus {
  connection_id: string;
  contacts: ProspectContactCrmStatus[];
  summary: {
    total_contacts: number;
    exported_contacts: number;
    enrolled_contacts: number;
    replied_contacts: number;
    contacts_with_deal: number;
    by_stage: { stage: string; label: string; count: number }[];
  };
}

export interface CrmSequenceOption {
  sequence_id: string;
  connection_id: string;
  name: string | null;
  owner_user_id: string | null;
}

export interface CrmExportJob {
  id: string;
  collection_id: string;
  connection_id: string;
  status: "pending" | "running" | "completed" | "failed";
  options: Record<string, unknown>;
  matched_count: number | null;
  companies_exported: number;
  contacts_exported: number;
  contacts_enrolled: number;
  skipped_count: number;
  failed_count: number;
  crm_company_list_id: string | null;
  crm_contact_list_id: string | null;
  result: { errors?: { message: string | null }[] };
  error: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProspectListContactEntry {
  id: string;
  collection_id: string | null;
  master_contact_id: string;
  contact_name: string | null;
  /** Current title at master_company_id, from the person's open employment stint. */
  title: string | null;
  /** From the person's newest bought profile. */
  headline: string | null;
  photo_url: string | null;
  reachability: ContactReachability;
  master_company_id: string | null;
  /** The company entry the person belongs to; the only tie to an unresolved company. */
  company_entry_id: string | null;
  company_name: string | null;
  added_by_user_id: string | null;
  added_by_agent_run_id: string | null;
  added_with_method: string;
  created_at: string;
  disqualified_at: string | null;
  disqualified_by_user_id: string | null;
  disqualified_by_agent_run_id: string | null;
  disqualification_reason: string | null;
  accepted_at: string | null;
  accepted_by_user_id: string | null;
  accepted_by_agent_run_id: string | null;
  inclusion_reason: string | null;
  note: string | null;
  labels: Record<string, string>;
  signal_trigger_count: number;
  /** Required contact property keys this contact is missing. */
  missing_required_properties: string[];
}

/** Raw fields for an interactive contact — used to resolve or create a person. */
export interface ContactInput {
  first_name?: string | null;
  last_name?: string | null;
  full_name?: string | null;
  email?: string | null;
  phone?: string | null;
  linkedin?: string | null;
  region_hint?: string | null;
}

export interface SuggestedMatch {
  master_contact_id: string;
  full_name: string | null;
  tier: string;
  matched_value: string;
  name_score: number | null;
  method: string;
  confidence: number | null;
  verified: boolean;
  acceptable: boolean;
  reachability: ContactReachability;
}

export interface ContactResolveResponse {
  resolved_name: string | null;
  has_identifier: boolean;
  can_create: boolean;
  recommended_action: "review" | "create";
  matches: SuggestedMatch[];
}

export interface AddContactPayload extends ContactInput {
  /** Link an existing person (merge). Omit to create from the fields above. */
  master_contact_id?: string | null;
  master_company_id?: string | null;
  /** Instead of master_company_id, for a company the database does not hold. */
  company_entry_id?: string | null;
  reason?: string | null;
  note?: string | null;
  labels?: Record<string, string>;
}

export interface ProspectListOverview {
  id: string;
  name: string;
  tenant_id: string;
  tenant_name: string;
  tenant_logo_url: string | null;
  prospect_count: number;
  signal_trigger_count: number;
  pending_count: number;
  added_24h: number;
  added_7d: number;
  added_30d: number;
  daily_added_14d: number[];
  /** Non-disqualified companies missing a required property. */
  incomplete_count: number;
  collection_type: ProspectListType;
  created_at: string;
  created_by_user_id: string | null;
  created_by_display_name: string | null;
  created_by_email: string | null;
  created_by_picture_url: string | null;
  last_entry_at: string | null;
  archived: boolean;
  origin: ProspectListOrigin | null;
  collaborators: UserBrief[];
  // Runs working on the collection: its own, its order's, its workflow's.
  active_agent_runs: ActiveAgentRun[];
}

export interface EnrichmentFieldChange {
  field: string;
  current: string | null;
  proposed: string;
}

export interface EnrichmentPreviewEntry {
  entry_id: string;
  master_company_id: string;
  company_name: string | null;
  fields: EnrichmentFieldChange[];
  reason: string | null;
}

export interface EnrichMasterFromLabelsPreview {
  blanks: EnrichmentPreviewEntry[];
  conflicts: EnrichmentPreviewEntry[];
  /** Changes that cannot be written, e.g. a domain held by another company. */
  blocked: EnrichmentPreviewEntry[];
}

export interface EnrichMasterFromLabelsResult {
  requested: number;
  updated: number;
  noop: number;
  errors: { entry_id: string; message: string }[];
  skipped: { entry_id: string; message: string }[];
}

// Platform health types (api/schemas/platform_health.py)
export type ImportSourceStatus = "ok" | "running" | "failed" | "overdue" | "paused" | "disabled" | "never";

/** One `platform_import_stats` row; what `matched` counts depends on the domain. */
export interface ImportStatRow {
  domain: string;
  source: string;
  country: string;
  total: number;
  matched: number | null;
  added_24h: number | null;
  added_7d: number | null;
  refreshed_24h: number | null;
  last_at: string | null;
  latest_year: number | null;
}

export interface ImportSourceHealth {
  key: string;
  label: string;
  group: string;
  schedule: string;
  status: ImportSourceStatus;
  detail: string | null;
  last_run_at: string | null;
  last_run_status: string | null;
  last_run_records: number | null;
  last_success_at: string | null;
  last_error: string | null;
  failures_7d: number;
  total: number | null;
  added_24h: number | null;
  added_7d: number | null;
  refreshed_24h: number | null;
}

export interface CrmConnectionHealth {
  id: string;
  name: string;
  provider: string;
  status: ImportSourceStatus;
  last_run_at: string | null;
  last_run_status: string | null;
  last_success_at: string | null;
  last_error: string | null;
}

export interface PlatformHealth {
  generated_at: string;
  stats_refreshed_at: string | null;
  sources: ImportSourceHealth[];
  stats: ImportStatRow[];
  crm_connections: CrmConnectionHealth[];
  news_feeds: {
    enabled: number;
    failing: number;
    last_success_at: string | null;
    worst: { name: string; consecutive_failures: number; last_success_at: string | null; last_error: string | null }[];
  };
  csv_imports: {
    id: string;
    source: string;
    file_name: string;
    status: string;
    row_count: number | null;
    imported_count: number | null;
    created_at: string;
    completed_at: string | null;
  }[];
}

export interface QueuePoint {
  at: string;
  pending: number | null;
  in_flight: number | null;
}

/** One data type's rows in the signal inbox, as of the newest five-minute sample. */
export interface SignalInboxBacklog {
  data_type: string;
  label: string;
  active_workflows: number;
  pending: number;
  in_flight: number;
  oldest_pending_at: string | null;
  processed_1h: number | null;
  processed_24h: number | null;
  failed_24h: number | null;
  added_1h: number | null;
  added_24h: number | null;
  // Swept because no workflow watched the type.
  skipped_24h: number | null;
  // At the last hour's net rate; null when the backlog isn't shrinking.
  hours_to_drain: number | null;
  trend: QueuePoint[];
  // Evaluations (one record decided for one workflow) over the last whole hour / day:
  // by rules, ruled out by the decision model's screen, or by the LLM evaluator.
  evaluations_1h: number;
  evaluations_24h: number;
  rules_1h: number;
  screened_out_1h: number;
  llm_evaluated_1h: number;
  // Records that reached at least one workflow in the last whole hour.
  records_evaluated_1h: number;
}

export type InferenceInstanceKind = "chat" | "decision";

export interface InferenceInstanceLoad {
  id: string;
  hostname: string;
  gpu_name: string | null;
  model_name: string | null;
  /** chat = OpenAI chat completions; decision = a SystemOne decision model (signal screening). */
  kind: InferenceInstanceKind;
  weight: number;
  state: "serving" | "stalled" | "offline";
  last_heartbeat_at: string | null;
  running: number | null;
  waiting: number | null;
  kv_cache_usage_pct: number | null;
  requests_24h: number | null;
  generation_tokens_1h: number | null;
  tokens_per_second_24h: number | null;
  // pending = waiting, in_flight = running.
  trend: QueuePoint[];
}

export interface LlmPurposeLoad {
  purpose: string;
  calls_1h: number;
  calls_24h: number;
  errors_1h: number;
  errors_24h: number;
  p50_ms: number | null;
  p95_ms: number | null;
}

export interface PlatformQueues {
  generated_at: string;
  sampled_at: string | null;
  signal_inbox: SignalInboxBacklog[];
  inference: InferenceInstanceLoad[];
  llm: LlmPurposeLoad[];
  engine: {
    enabled: boolean;
    serving_instances: number;
    llm_slots_per_instance: number;
    llm_slots: number;
    drain_seconds: number;
  };
  classifier: {
    enabled: boolean;
    pending: number | null;
    pending_is_floor: boolean;
    sampled_at: string | null;
  };
}

/** One day (UTC) of a signal workflow, summed from its hourly samples. */
export interface SignalWorkflowDay {
  day: string;
  evaluated: number;
  matched: number;
  // Matched signals whose latest research verdict qualifies, by the brief's day.
  qualified: number;
  failed: number;
  // The most it had waiting that day.
  pending: number;
  // Tokens in + out of its evaluator calls (none in samples older than the field).
  tokens: number;
}

/** One signal workflow's backlog and output, as of its newest sample. */
export interface SignalWorkflowLoad {
  id: string;
  name: string;
  data_type: string;
  label: string;
  match_mode: string;
  active: boolean;
  active_since: string | null;
  // Pending rows of its type queued since its cutoff: an upper bound (source
  // filters drop some only when evaluated). Null before it was first sampled.
  pending: number | null;
  oldest_pending_at: string | null;
  // At its data type's net rate: the engine drains rows, not workflows.
  hours_to_drain: number | null;
  evaluated_1h: number | null;
  evaluated_24h: number | null;
  matched_24h: number | null;
  failed_24h: number | null;
  p50_ms: number | null;
  // Its use of the rigs: tokens in + out and model time (summed call latency).
  tokens_24h: number | null;
  model_ms_24h: number | null;
  // The window's totals (`days`).
  evaluated: number;
  matched: number;
  qualified: number;
  tokens: number;
  trend: QueuePoint[];
  daily: SignalWorkflowDay[];
}

export interface TenantSignalWorkflows {
  tenant_id: string;
  tenant_name: string;
  tenant_slug: string;
  organization_name: string | null;
  workflows: SignalWorkflowLoad[];
}

export interface PlatformSignalWorkflows {
  generated_at: string;
  sampled_at: string | null;
  days: number;
  tenants: TenantSignalWorkflows[];
}

/** A durable task: one a recurring job's tick waits on, or one in the runner's queue. */
export interface RecurringTask {
  id: string;
  kind: string;
  status: string;
  attempt: number;
  max_attempts: number;
  lock_key: string | null;
  created_at: string;
  started_at: string | null;
  heartbeat_at: string | null;
  lease_owner: string | null;
  checkpoint: unknown;
}

/** How a recurring job, or one chain of it, is doing (`platform_health/recurring.py`). */
export type RecurringStatus = "ok" | "late" | "failing" | "running long" | "no runs" | "stopped" | "off";

/** One chain of a recurring job: its key (`main` for a job with one chain). */
export interface RecurringChain {
  key: string;
  label: string;
  status: RecurringStatus;
  last_run_at: string | null;
  last_finished_at: string | null;
  last_outcome: string | null;
  last_duration_s: number | null;
  last_error: string | null;
  next_run_at: string | null;
  running_since: string | null;
  runs_24h: number;
  failures_24h: number;
  task: RecurringTask | null;
}

export interface RecurringJob {
  name: string;
  /** What people call it; null: derive it from `name` (`jobTitle`). */
  title: string | null;
  group: string;
  description: string | null;
  /** What the job is for (`durable.categories.JobCategory`) and its label. */
  category: string;
  category_label: string;
  /** The one country whose data it syncs (alpha-2, `EU`), else null. */
  country: string | null;
  every_s: number;
  aligned: boolean;
  keyed: boolean;
  switch_key: string | null;
  switch_on: boolean;
  status: RecurringStatus;
  last_run_at: string | null;
  next_run_at: string | null;
  running: number;
  runs_24h: number;
  failures_24h: number;
  p50_s: number | null;
  p95_s: number | null;
  /** 168 hourly buckets, oldest first. */
  hourly: { hour: string; ran: number; failed: number }[];
  chains: RecurringChain[];
}

export interface TaskKindStats {
  kind: string;
  queued: number;
  running: number;
  oldest_queued_s: number | null;
  succeeded_24h: number;
  failed_24h: number;
  p95_s: number | null;
}

export interface PlatformRecurring {
  generated_at: string;
  /** Whether Restate answered: the running and next ticks come from it. */
  live: boolean;
  live_error: string | null;
  jobs: RecurringJob[];
  task_kinds: TaskKindStats[];
  runners: { owner: string; running: number }[];
  running_tasks: RecurringTask[];
  recent_failures: { id: string; kind: string; status: string; error: string | null; attempt: number; finished_at: string }[];
}

export interface RecurringTick {
  key: string;
  started_at: string;
  finished_at: string;
  duration_s: number;
  outcome: string;
  error: string | null;
  next_at: string | null;
}

export interface PlatformRecurringTicks {
  job: string;
  ticks: RecurringTick[];
}

/** What contact-data providers did for one tenant in a month. */
export interface DataProviderCounts {
  // Data points delivered (ledger rows, priced or free).
  people: number;
  work_emails: number;
  phones: number;
  company_profiles: number;
  // Purchases and company lookups made, found or not.
  purchases: number;
  lookups: number;
  // Agents' people searches and the candidates they returned.
  searches: number;
  search_rows: number;
  // Billed amount (USD); always 0 for a provider that is not billed.
  spend: number;
}

export interface DataProviderUsageRow extends DataProviderCounts {
  organization_id: string;
  organization_name: string;
  tenant_id: string;
  tenant_name: string;
  provider: string;
  provider_label: string;
  billed: boolean;
}

export interface DataProviderTotals extends DataProviderCounts {
  provider: string;
  provider_label: string;
  billed: boolean;
  tenants: number;
}

export interface DataProviderUsage {
  month: string;
  rows: DataProviderUsageRow[];
  providers: DataProviderTotals[];
}

/** One of GetLeads' row budgets (search, export and enrichment combined). */
export interface FairUseWindow {
  cap: number | null;
  used: number | null;
  remaining: number | null;
  resets_at: string | null;
}

/** The GetLeads account against its fair-use limits. */
export interface DataProviderLimits {
  provider: string;
  provider_label: string;
  // False: no key stored; `error` says so.
  configured: boolean;
  // GetLeads couldn't be asked; the request counters may still be there.
  error: string | null;
  plan: string | null;
  // False on plans billed in credits (then `credits_remaining` counts instead).
  applies: boolean | null;
  credits_remaining: number | null;
  daily: FairUseWindow | null;
  monthly: FairUseWindow | null;
  // The window that limits the account first: "daily" | "monthly".
  binding: string | null;
  // Our budget (the provider setting) and the account's default limit.
  rpm_budget: number;
  rpm_account: number;
  // Per minute, oldest first, the current (unfinished) minute last; empty when
  // the counters can't be read.
  requests_per_minute: number[];
  rate_limited_per_minute: number[];
  fetched_at: string;
}

// Tenant types
export interface Tenant {
  id: string;
  name: string;
  slug: string;
  /** The company's website, a bare host ("acme.no"); onboarding researches it. */
  domain: string | null;
  /** What the company sells, 5–7 sentences at most. */
  offering_text: string | null;
  icp_text: string | null;
  /** The people the company sells to. */
  buyer_persona_text: string | null;
  logo_url: string | null;
  required_company_properties: string[];
  required_contact_properties: string[];
  /** ISO 4217 code money is shown and filtered in for this tenant. */
  currency: string;
  created_at: string;
  updated_at: string;
}

/** The tenant profile; only the fields sent change. */
export interface TenantIcpUpdate {
  offering_text?: string | null;
  icp_text?: string | null;
  buyer_persona_text?: string | null;
}

export type TenantProfileRunStage =
  | "queued"
  | "looking_up"
  | "researching"
  | "fetching_logo"
  | "saving"
  | "designing"
  | "done";

/** A workflow the onboarding designer created (paused), as the run reports it. */
export interface DesignedWorkflow {
  id?: string;
  name: string;
  trigger_type: string;
  data_type: string | null;
  rationale: string | null;
  /** Set on a proposal that failed the workflow checks and was not created. */
  error?: string;
}

/**
 * One run on a tenant's profile: "research" drafts the profile and logo from the
 * domain (and, at onboarding, workflows); "design" only designs workflows.
 */
export interface TenantProfileRun {
  id: string;
  tenant_id: string;
  kind: "research" | "design";
  domain: string | null;
  status: "pending" | "running" | "completed" | "failed";
  /** 0–100. */
  progress: number;
  stage: TenantProfileRunStage;
  /** What the agent is doing right now, in one line. */
  activity: string | null;
  error: string | null;
  output: {
    offering?: string | null;
    icp?: string | null;
    buyer_persona?: string | null;
    notes?: string | null;
    applied?: string[];
    workflows?: DesignedWorkflow[];
    workflows_rejected?: DesignedWorkflow[];
    workflow_notes?: string | null;
    workflow_design_error?: string;
  } | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

export interface TenantRequiredPropertiesUpdate {
  required_company_properties: string[];
  required_contact_properties: string[];
}

export interface PropertyCatalogField {
  key: string;
  label: string;
}

export interface PropertyCatalogResponse {
  company: PropertyCatalogField[];
  contact: PropertyCatalogField[];
}

export interface TenantCreate {
  name: string;
  organization_id: string;
  /** Set, it starts onboarding research. */
  domain?: string | null;
}

export interface TenantCreateResponse extends Tenant {
  profile_run: TenantProfileRun | null;
  /** Why research did not start despite a domain (no Claude token, …). */
  profile_research_unavailable: string | null;
}

export interface TenantUpdate {
  name?: string;
  /** Empty string or null clears it. */
  domain?: string | null;
}

export interface TenantCounts {
  contacts: number;
  companies: number;
  deals: number;
  activities: number;
}

export interface TenantStatsResponse {
  tenant_id: string;
  counts: TenantCounts;
}

export interface TenantDataRecord {
  id: string;
  external_id: string;
  properties: Record<string, unknown>;
  created_at: string;
}

export interface TenantDataResponse {
  object_type: string;
  total: number;
  records: TenantDataRecord[];
}

// Provider types
export interface CredentialField {
  name: string;
  label: string;
  field_type: string;
  required: boolean;
  help_text: string;
}

export interface Provider {
  id: string;
  name: string;
  credential_fields: CredentialField[];
}

// Connection types
export interface ScopeIssue {
  scope: string;
  last_endpoint: string | null;
  last_seen: string | null;
  first_seen: string | null;
  count: number | null;
}

export interface ConnectionHealth {
  status: "healthy" | "degraded";
  missing_scopes: string[];
  issues: ScopeIssue[];
}

export interface Connection {
  id: string;
  name: string | null;
  provider: string;
  config: Record<string, unknown>;
  credentials_hint: Record<string, string>;
  status: string;
  health: ConnectionHealth;
  created_at: string;
  updated_at: string;
}

export interface CreateConnectionInput {
  provider: string;
  name?: string;
  credentials: Record<string, unknown>;
  config: Record<string, unknown>;
}

export interface SequenceAccessResult {
  status: "ok" | "error" | "missing_scopes" | "unsupported";
  provider?: string;
  user_id?: string;
  sequence_count?: number;
  sequences?: { id: string; name: string | null }[];
  sender_status?: "not_checked" | "resolvable" | "unknown_sender";
  sender_detail?: string | null;
  scopes?: string[];
  detail?: string;
}

// Mapping types
export interface ConnectionMapping {
  id: string;
  connection_id: string;
  tenant_id: string;
  pipeline_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateMappingInput {
  tenant_id: string;
  pipeline_id?: string;
}

// Slack types
export interface SlackIntegration {
  id: string;
  organization_id: string;
  team_id: string;
  team_name: string;
  enterprise_id: string | null;
  bot_user_id: string;
  scopes: string[];
  authed_user_id: string;
  app_id: string;
  created_at: string;
  updated_at: string;
}

// Notion types
export interface NotionSetupInfo {
  // Whether the platform has a Notion app (client id/secret) set up.
  configured: boolean;
  redirect_uri: string;
  webhook_url: string;
}

export interface NotionConnection {
  id: string;
  organization_id: string;
  workspace_id: string;
  workspace_name: string | null;
  workspace_icon: string | null;
  // "needs_reconnect": the token stopped working; install again.
  status: "active" | "needs_reconnect";
  last_error: string | null;
  last_polled_at: string | null;
  created_at: string;
}

export type NotionObjectType = "page" | "data_source";

// A page or database shared with the app, as search returns it.
export interface NotionObject {
  notion_id: string;
  object_type: NotionObjectType;
  title: string;
  url: string | null;
}

// Which shared pages one tenant uses: a page counts when an include scope
// covers it and no exclude scope does.
export interface NotionScopeInput {
  notion_object_id: string;
  object_type: NotionObjectType;
  mode: "include" | "exclude";
  // Pages only: the pages under it too.
  include_subpages: boolean;
  use_for_triggers: boolean;
  use_for_agents: boolean;
  // Databases only: the property holding the company's website or email.
  company_property: string | null;
}

export interface NotionScope extends NotionScopeInput {
  id: string;
  connection_id: string;
  tenant_id: string;
  title: string;
  url: string | null;
}

// Pipeline types
export interface Pipeline {
  pipeline_id: string;
  connection_id: string;
  name: string;
}

/** A CRM login email domain, and the client tenant its logins work for. */
export interface OwnerDomain {
  email_domain: string;
  tenant_id: string | null;
  logins: string[];
  calls_90d: number;
}

export interface CrmRepIdentity {
  id: string;
  /** "owner" is a CRM login; "dialer_agent" a caller name the dialer logged. */
  kind: "owner" | "dialer_agent";
  key: string;
  label: string;
  source: "auto" | "manual";
}

/** A person behind CRM activity, across their logins and dialer names. */
export interface CrmRep {
  id: string;
  display_name: string;
  identities: CrmRepIdentity[];
  calls_90d: number;
}

// Master Company Stats types
export interface CountryCount {
  country: string;
  count: number;
  with_website: number;
  with_crawl: number;
  with_classification: number;
  added_24h?: number;
  website_added_24h?: number;
  crawl_added_24h?: number;
  classification_added_24h?: number;
}

export interface SignalingDataTypeStats {
  data_type: string;
  label: string;
  total_records: number;
  records_added_24h: number;
  inbox_processed: number;
  inbox_pending: number;
  inbox_processed_24h: number;
  signals_matched: number;
  signals_total: number;
  signals_matched_24h: number;
  has_active_rules: boolean;
}

export interface MasterCompanyStats {
  total_companies: number;
  companies_with_website: number;
  companies_without_website: number;
  websites_crawled: number;
  companies_by_country: CountryCount[];
  companies_added_24h: number;
  websites_found_24h: number;
  websites_crawled_24h: number;
  total_job_ads: number;
  job_ads_matched: number;
  job_ads_added_24h: number;
  technologies_scanned: number;
  technologies_scanned_24h: number;
  companies_classified: number;
  companies_classified_24h: number;
  linkedin_urls_found: number;
  linkedin_urls_found_24h: number;
  signaling: SignalingDataTypeStats[];
}

export interface JobAd {
  id: string;
  source: string;
  external_id: string;
  title: string;
  company_name: string;
  country: string | null;
  city: string | null;
  state: string | null;
  url: string | null;
  website: string | null;
  remote: boolean | null;
  published_at: string | null;
  source_data: Record<string, unknown> | null;
  match_method: string | null;
  match_confidence: number | null;
  created_at: string;
  updated_at: string;
}

export interface JobAdDetail extends JobAd {
  master_company_id: string | null;
  description: string | null;
  domain: string | null;
}

export interface JobAdListItem {
  id: string;
  source: string;
  title: string;
  company_name: string;
  country: string | null;
  city: string | null;
  url: string | null;
  remote: boolean | null;
  published_at: string | null;
  master_company_id: string | null;
  match_confidence: number | null;
  created_at: string;
}

export type JobAdListResponse = Paginated<JobAdListItem>;

export interface JobAdFilterOptions {
  countries: string[];
  sources: string[];
}

export interface SourceStat {
  source: string;
  count: number;
}

export interface SourceStatsResponse {
  items: SourceStat[];
}

export interface JobAdListParams {
  title?: string;
  company_name?: string;
  country?: string[];
  source?: string[];
  remote?: boolean;
  matched?: boolean;
  published_from?: string;
  published_to?: string;
  sort_by?: string;
  sort_dir?: string;
  limit?: number;
  offset?: number;
}

// Regulatory Filing types
export interface RegulatoryFilingListItem {
  id: string;
  source: string;
  country: string;
  external_id: string;
  title: string;
  applicant_name: string;
  filing_type: string | null;
  filing_subtype: string | null;
  status: string;
  status_raw: string | null;
  is_active: boolean;
  filed_at: string | null;
  decision_at: string | null;
  hearing_deadline: string | null;
  region: string | null;
  subregion: string | null;
  capacity_mw: number | null;
  estimated_production_gwh: number | null;
  source_url: string | null;
  master_company_id: string | null;
  master_company_name: string | null;
  match_method: string | null;
  match_confidence: number | null;
  created_at: string;
}

export type RegulatoryFilingListResponse = Paginated<RegulatoryFilingListItem>;

export interface CompanyNewsMention {
  article_id: string;
  title: string | null;
  document_url: string;
  source_common_name: string | null;
  language: string | null;
  published_at: string | null;
  roles: string[];
  runs: number;
}

export type CompanyNewsMentionListResponse = Paginated<CompanyNewsMention>;

export interface CompanyPressRelease {
  id: string;
  title: string;
  url: string | null;
  source: string;
  issuer_name: string;
  published_at: string;
  language: string | null;
  categories: { key: string; label: string }[];
  is_regulatory: boolean;
  match_method: string | null;
  snippet: string | null;
}

export type CompanyPressReleaseListResponse = Paginated<CompanyPressRelease>;

export interface RegulatoryFilingDetail extends RegulatoryFilingListItem {
  source_data: Record<string, unknown> | null;
  updated_at: string;
}

export interface RegulatoryFilingFilterOptions {
  countries: string[];
  sources: string[];
  statuses: string[];
}

export interface RegulatoryFilingListParams {
  title?: string;
  applicant_name?: string;
  country?: string[];
  source?: string[];
  status?: string[];
  is_active?: boolean;
  matched?: boolean;
  filed_from?: string;
  filed_to?: string;
  hearing_from?: string;
  hearing_to?: string;
  min_capacity_mw?: number;
  sort_by?: string;
  sort_dir?: string;
  limit?: number;
  offset?: number;
}

export interface CompanyRegulatoryFilingParams {
  status?: "active" | "completed" | "all";
  keyword?: string;
  source?: string[];
  limit?: number;
  offset?: number;
}

// CPV codes
export interface CpvCodeItem {
  code: string;
  lang: string;
  description: string;
}

export interface CpvCodeListResponse {
  items: CpvCodeItem[];
}

// Tenders
export interface TenderListItem {
  id: string;
  source: string;
  notice_id: string;
  procedure_id: string | null;
  version: number;
  notice_type: string;
  notice_subtype: string | null;
  country: string;
  language: string | null;
  buyer_name: string | null;
  buyer_org_number: string | null;
  buyer_country: string | null;
  buyer_company_id: string | null;
  buyer_company_name: string | null;
  buyer_match_method: string | null;
  buyer_match_confidence: number | null;
  winner_name: string | null;
  winner_org_number: string | null;
  winner_country: string | null;
  winner_company_id: string | null;
  winner_company_name: string | null;
  winner_match_method: string | null;
  winner_match_confidence: number | null;
  winner_is_sme: boolean | null;
  num_tenders_received: number | null;
  title: string;
  cpv_codes: string[] | null;
  estimated_value: string | null;
  awarded_value: string | null;
  currency: string | null;
  procedure_type: string | null;
  is_framework_agreement: boolean | null;
  publication_date: string | null;
  submission_deadline: string | null;
  award_date: string | null;
  contract_signature_date: string | null;
  source_url: string | null;
  created_at: string;
}

export type TenderListResponse = Paginated<TenderListItem>;

export interface TenderDocument {
  id: string;
  external_url: string;
  filename: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  fetch_status: string;
  fetch_method: string | null;
  fetched_at: string | null;
  download_url: string | null;
}

export interface TenderDetail extends TenderListItem {
  description: string | null;
  source_data: Record<string, unknown> | null;
  updated_at: string;
  documents: TenderDocument[];
}

export interface TenderFilterOptions {
  countries: string[];
  sources: string[];
  notice_types: string[];
  procedure_types: string[];
}

export interface TenderListParams {
  q?: string;
  buyer?: string;
  winner?: string;
  country?: string[];
  source?: string[];
  notice_type?: string[];
  procedure_type?: string[];
  is_framework_agreement?: boolean;
  buyer_matched?: boolean;
  winner_matched?: boolean;
  published_from?: string;
  published_to?: string;
  deadline_from?: string;
  deadline_to?: string;
  award_from?: string;
  award_to?: string;
  min_value?: string;
  max_value?: string;
  cpv?: string;
  sort_by?: string;
  sort_dir?: string;
  limit?: number;
  offset?: number;
}

// News feeds (developer only): RSS / sitemap sources polled into news_articles.
export type NewsFeedKind = "rss" | "news_sitemap" | "sitemap";

export interface NewsFeedStats {
  new_24h: number;
  new_7d: number;
  /** Articles the feed found that APITube already had. */
  duplicates_7d: number;
  with_body_7d: number;
  awaiting_content: number;
}

export interface NewsFeed {
  id: string;
  name: string;
  /** May contain `{year}` / `{month}` (monthly sitemaps) or `{date}` (daily sitemaps). */
  url: string;
  kind: NewsFeedKind;
  source_domain: string;
  source_country: string | null;
  language: string | null;
  enabled: boolean;
  fetch_content: boolean;
  poll_interval_minutes: number;
  last_fetched_at: string | null;
  last_success_at: string | null;
  last_error: string | null;
  consecutive_failures: number;
  last_item_count: number | null;
  last_new_count: number | null;
  last_duplicate_count: number | null;
  created_at: string;
  stats: NewsFeedStats;
}

export interface NewsFeedCreate {
  name: string;
  url: string;
  kind?: NewsFeedKind;
  source_domain?: string | null;
  source_country?: string | null;
  language?: string | null;
  enabled?: boolean;
  fetch_content?: boolean;
  poll_interval_minutes?: number;
}

export type NewsFeedUpdate = Partial<NewsFeedCreate>;

export interface NewsFeedPreviewItem {
  url: string;
  canonical_url: string | null;
  title: string | null;
  description: string | null;
  published_at: string | null;
  has_body: boolean;
  /** "apitube" | "news_feed" when already stored, null when new. */
  existing_source: string | null;
}

export interface NewsFeedPreview {
  item_count: number;
  items: NewsFeedPreviewItem[];
}

// News article types
export interface NewsCategory {
  id: number | string;
  name: string;
  score?: number;
  taxonomy?: string;
  links?: Record<string, string>;
}

export interface NewsTopic {
  id: string;
  name: string;
  score?: number;
  links?: Record<string, string>;
}

export interface NewsArticleListItem {
  id: string;
  source: string;
  external_id: string;
  document_url: string;
  source_common_name: string | null;
  source_domain: string | null;
  title: string | null;
  description: string | null;
  image_url: string | null;
  author_name: string | null;
  language: string | null;
  source_country: string | null;
  published_at: string | null;
  sentiment_overall: number | null;
  sentiment_polarity: string | null;
  read_time_minutes: number | null;
  words_count: number | null;
  is_breaking: boolean | null;
  is_duplicate: boolean | null;
  is_free: boolean | null;
  story_id: number | null;
  categories: NewsCategory[] | null;
  topics: NewsTopic[] | null;
  keywords: string[] | null;
  extraction_status: string;
  created_at: string;
}

export interface NewsArticleDetail extends NewsArticleListItem {
  body: string | null;
  industries: unknown[] | null;
  media: unknown[] | null;
  links_external: unknown[] | null;
  locations_mentioned: unknown[] | null;
  readability: Record<string, unknown> | null;
  sentiment: Record<string, unknown> | null;
  summary: unknown[] | null;
  source_external_id: number | null;
  source_home_page_url: string | null;
  source_bias: string | null;
  source_rank_opr: number | null;
  author_external_id: number | null;
  shares_total: number | null;
  characters_count: number | null;
  sentences_count: number | null;
  paragraphs_count: number | null;
  source_data: Record<string, unknown> | null;
  updated_at: string;
}

export type NewsArticleListResponse = Paginated<NewsArticleListItem>;

export interface NewsArticleFilterOptions {
  sources: string[];
  source_countries: string[];
  languages: string[];
  top_domains: string[];
  top_categories: Array<{ id: number | string; name: string; count: number }>;
  top_topics: Array<{ id: string; name: string; count: number }>;
  top_keywords: string[];
}

export interface NewsArticleListParams {
  q?: string;
  source?: string[];
  source_country?: string[];
  language?: string[];
  domain?: string[];
  category_id?: string[];
  topic_id?: string[];
  keyword?: string[];
  extraction_status?: string[];
  story_id?: number;
  published_from?: string;
  published_to?: string;
  min_sentiment?: number;
  max_sentiment?: number;
  sort_by?: string;
  sort_dir?: string;
  limit?: number;
  offset?: number;
}

// ATS Feed types
export interface AtsFeedListItem {
  id: string;
  ats: string;
  domain: string;
  feed_url: string;
  is_subdomain: boolean;
  verified: boolean | null;
  last_checked_at: string | null;
  discovery_source: string;
  http_status: number | null;
  error: string | null;
  created_at: string;
  last_crawled_at: string | null;
  job_count: number | null;
}

export interface AtsFeedCrawlStats {
  crawled_last_24h: number;
  lag: number;
  total_verified: number;
  total_jobs_last_24h: number;
}

export type AtsFeedListResponse = Paginated<AtsFeedListItem>;

export interface AtsFeedFilterOptions {
  ats_providers: string[];
  discovery_sources: string[];
}

export interface AtsFeedAtsStat {
  ats: string;
  count: number;
  failed_count: number;
}

export interface AtsFeedAtsStatsResponse {
  items: AtsFeedAtsStat[];
}

export interface AtsFeedListParams {
  domain?: string;
  ats?: string[];
  discovery_source?: string[];
  verified?: boolean;
  is_subdomain?: boolean;
  sort_by?: string;
  sort_dir?: string;
  limit?: number;
  offset?: number;
}

// Master Company types
export interface MasterCompanyDetail {
  id: string;
  name: string;
  country: string;
  org_number: string | null;
  domain: string | null;
  website: string | null;
  linkedin_url: string | null;
  industry: string | null;
  org_form: string | null;
  phone: string | null;
  email: string | null;
  address: Record<string, unknown> | null;
  employee_count: number | null;
  employee_size_bucket: string | null;
  description: string | null;
  ai_summary: string | null;
  ai_industries: string[] | null;
  ai_served_verticals: string[] | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  field_updated_at: Record<string, string>;
}

export interface MasterCompanyListItem {
  id: string;
  name: string;
  country: string;
  org_number: string | null;
  industry: string | null;
  ai_industries: string[] | null;
  ai_served_verticals: string[] | null;
  org_form: string | null;
  employee_count: number | null;
  employee_size_bucket: string | null;
  /** In the response's `currency`. */
  revenue: number | null;
  operating_result: number | null;
  /** As filed, in `reported_currency`. */
  reported_revenue: number | null;
  reported_operating_result: number | null;
  reported_currency: string | null;
  fiscal_year: number | null;
  created_at: string;
  updated_at: string;
}

export type MasterCompanyListResponse = CursorPaginated<MasterCompanyListItem> & {
  currency?: string | null;
};

export interface FilterOption {
  code: string;
  label: string;
}

export interface FilterOptions {
  countries: string[];
  ai_industries: string[];
  ai_served_verticals: string[];
  org_forms: string[];
  employee_size_buckets: string[];
  technologies: string[];
  contact_role_types: FilterOption[];
}

export interface CompanyListParams {
  country?: string[];
  name?: string;
  industry_keywords?: string[];
  served_vertical_keywords?: string[];
  org_form?: string[];
  employee_size_bucket?: string[];
  job_ad_keywords?: string[];
  technology?: string[];
  contact_role_type?: string[];
  contact_name?: string;
  revenue_min?: number;
  revenue_max?: number;
  operating_result_min?: number;
  operating_result_max?: number;
  /** Unit of the money bounds and of revenue in the result. */
  currency?: string;
  regulatory_filing_status?: "active" | "completed" | "any";
  regulatory_filing_keyword?: string;
  regulatory_filing_source?: string[];
  sort_by?: string;
  sort_dir?: "asc" | "desc";
  limit?: number;
  offset?: number;
  cursor?: string;
}

export interface BulkAddJob {
  id: string;
  collection_id: string;
  status: "pending" | "running" | "completed" | "failed";
  matched_count: number | null;
  added_count: number;
  error: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface CompanyContact {
  contact_id: string;
  contact_type: string;
  first_name: string | null;
  middle_name: string | null;
  last_name: string | null;
  full_name: string;
  birth_date: string | null;
  role_type: string;
  role_title_en: string | null;
  role_title_no: string | null;
  from_date: string | null;
  to_date: string | null;
  is_active: boolean;
  source: string;
  created_at: string;
  email: string | null;
  phone: string | null;
  linkedin_url: string | null;
}

export interface MasterContactListItem {
  id: string;
  full_name: string;
  first_name: string | null;
  last_name: string | null;
  birth_year: number | null;
  city: string | null;
  country: string | null;
  has_email: boolean;
  created_at: string;
}

export type MasterContactListResponse = CursorPaginated<MasterContactListItem>;

export interface ContactReachability {
  email: string | null;
  phone: string | null;
  linkedin_url: string | null;
}

/** How a date was arrived at. Never render an inferred date as a stated one. */
export type DateBasis =
  | "declared"
  | "observed"
  | "inferred"
  | "source_changed_at"
  | "unknown";

export interface TitlePeriod {
  edge_id: string;
  title: string | null;
  seniority: string | null;
  from_date: string | null;
  to_date: string | null;
  from_basis: DateBasis | null;
  to_basis: DateBasis | null;
  first_seen_at: string | null;
  last_seen_at: string | null;
  is_current: boolean;
  source: string;
}

/** One continuous engagement at one company. A promotion is another
 *  TitlePeriod inside the same stint, not a second engagement. */
export interface EmploymentStint {
  stint_id: string;
  company_id: string | null;
  company_name: string | null;
  company_label: string | null;
  relation_type: string;
  role_title_en: string | null;
  current_title: string | null;
  seniority: string | null;
  started_on: string | null;
  ended_on: string | null;
  started_basis: DateBasis | null;
  ended_basis: DateBasis | null;
  is_current: boolean;
  sources: string[];
  titles: TitlePeriod[];
}

export interface ContactChannelHistoryItem {
  id: string;
  channel_type: string;
  value: string;
  source: string;
  confidence: number | null;
  is_verified: boolean;
  is_primary: boolean;
  is_active: boolean;
  verification_status: string | null;
  verified_at: string | null;
  first_seen_at: string | null;
  last_seen_at: string | null;
  deprecated_at: string | null;
  deprecated_reason: string | null;
  master_company_id: string | null;
  master_company_name: string | null;
}

export interface MasterContactDetail {
  id: string;
  contact_type: string;
  first_name: string | null;
  middle_name: string | null;
  last_name: string | null;
  full_name: string;
  birth_date: string | null;
  birth_year: number | null;
  city: string | null;
  country: string | null;
  is_deceased: boolean;
  employment: EmploymentStint[];
  reachability: ContactReachability;
  profile: ContactProfile | null;
}

export interface ContactProfileExperience {
  company?: string;
  company_domain?: string;
  company_linkedin_url?: string;
  title?: string;
  // ISO, as precise as the source: "2023", "2023-04", "2023-04-01".
  start?: string;
  end?: string;
  is_current: boolean;
  location?: string;
  description?: string;
}

export interface ContactProfileEducation {
  school: string;
  degree?: string;
  field?: string;
  start?: string;
  end?: string;
}

export interface ContactProfileCertification {
  name: string;
  issuer?: string;
  issued_on?: string;
}

/** What sources say about a person besides how to reach them, merged newest first. */
export interface ContactProfile {
  headline: string | null;
  about: string | null;
  photo_url: string | null;
  location: string | null;
  city: string | null;
  region: string | null;
  country: string | null;
  seniority: string | null;
  department: string | null;
  role_started_on: string | null;
  open_to_work: boolean | null;
  skills: string[];
  languages: string[];
  experience: ContactProfileExperience[];
  education: ContactProfileEducation[];
  certifications: ContactProfileCertification[];
  social_links: Record<string, string>;
  sources: { source: string; observed_at: string }[];
}

export interface ContactListParams {
  name?: string;
  role_type?: string[];
  company_id?: string;
  employed_at_company_id?: string;
  current_only?: boolean;
  title?: string;
  has_email?: boolean;
  sort_by?: string;
  sort_dir?: string;
  limit?: number;
  offset?: number;
  cursor?: string;
}

export interface CompanyEdge {
  edge_id: string;
  direction: "inbound" | "outbound";
  relation_type: string;
  relation_subtype: string;
  related_company_id: string | null;
  related_company_name: string | null;
  related_contact_id: string | null;
  related_contact_name: string | null;
  ownership_fraction: number | null;
  valid_from: string | null;
  valid_to: string | null;
  is_active: boolean;
  source: string;
  confidence: number | null;
}

export interface OwnershipTreeNode {
  company_id: string;
  company_name: string;
  parent_company_id: string | null;
  depth: number;
  direct_fraction: number | null;
  cumulative_fraction: number | null;
}

export interface OwnershipTreeResponse {
  direction: "down" | "up";
  nodes: OwnershipTreeNode[];
}

export interface CompanyTechnology {
  id: string;
  name: string;
  category: string | null;
  version: string | null;
  confidence: number;
}

export interface CompanySourceRecord {
  id: string;
  master_company_id: string | null;
  source: string;
  external_id: string;
  country: string | null;
  raw_data: Record<string, unknown>;
  confidence: number | null;
  fetched_at: string;
  created_at: string;
  updated_at: string;
}

export interface FinancialSourceFile {
  s3_key: string;
  filename: string;
  role: string;
}

export interface CompanyFinancial {
  id: string;
  master_company_id: string;
  org_number: string;
  fiscal_year: number;
  report_type: string;
  period_from: string | null;
  period_to: string | null;
  currency: string;
  revenue: number | null;
  operating_result: number | null;
  net_income: number | null;
  total_assets: number | null;
  total_equity: number | null;
  equity_ratio: number | null;
  employees_avg: number | null;
  profit_before_tax: number | null;
  net_financial_items: number | null;
  personnel_costs: number | null;
  tax_expense: number | null;
  cash: number | null;
  receivables: number | null;
  inventory: number | null;
  current_assets: number | null;
  fixed_assets: number | null;
  current_liabilities: number | null;
  long_term_liabilities: number | null;
  total_liabilities: number | null;
  share_capital: number | null;
  pledged_assets: number | null;
  proposed_dividend: number | null;
  audited: boolean | null;
  accounting_framework: string | null;
  parent_company: boolean | null;
  liquidation_basis: boolean | null;
  agm_date: string | null;
  audit_opinion: string | null;
  report_texts: Record<string, string> | null;
  raw_data: Record<string, unknown>;
  source_files: FinancialSourceFile[];
  fetched_at: string;
  created_at: string;
  updated_at: string;
}

// Decimal amounts arrive as strings.
export interface CompanyTaxAssessment {
  tax_year: number;
  source: string;
  taxable_income: string | null;
  taxes_assessed: string | null;
  tax_refund: string | null;
  residual_tax: string | null;
  tax_municipality: string | null;
  corrected: boolean;
}

export interface CompanyRegistryEvent {
  id: string;
  source: string;
  record_number: string;
  registered_on: string;
  notice_type: string | null;
  entry_codes: string[];
  kinds: string[];
  title: string | null;
  details: Record<string, unknown>;
  source_url: string | null;
}

// Crawl page types
export interface CrawlPageSummary {
  url: string;
  markdown_path: string;
  title: string | null;
  description: string | null;
  status_code: number | null;
  headings: string[];
  size_bytes_markdown: number | null;
  crawled_at: string | null;
  depth: number | null;
}

export interface CrawlPagesResponse {
  domain: string;
  master_company_id: string;
  total_size_bytes_markdown: number | null;
  pages: CrawlPageSummary[];
}

export interface CrawlPageContentResponse {
  url: string;
  title: string | null;
  markdown: string;
}

// CSV Import types
export interface CsvImport {
  id: string;
  source: string;
  file_name: string;
  s3_key: string;
  status: string;
  row_count: number | null;
  imported_count: number | null;
  duplicate_count: number | null;
  error: string | null;
  progress: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  attempt: number;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CsvImportUploadResponse {
  id: string;
  file_name: string;
  s3_key: string;
  status: string;
}

// Roles & permissions reference types
export interface RoleInfo {
  name: string;
  description: string;
  scopes: string[];
}

export interface ScopeInfo {
  name: string;
  description: string;
}

export interface RolePermissionLevel {
  level: string;
  roles: RoleInfo[];
  scopes: ScopeInfo[];
}

export interface RolesPermissionsResponse {
  levels: RolePermissionLevel[];
}

// Table Stats types
export interface TableStats {
  table_name: string;
  row_count: number;
  size_mb: number;
}

// AI Generation Log types
export interface AiGenerationLogItem {
  id: string;
  table_name: string;
  record_id: string;
  field_group: string;
  model: string;
  method: string;
  prompt_hash: string | null;
  generated_at: string;
  metadata: Record<string, unknown>;
}

export type AiGenerationLogsResponse = Paginated<AiGenerationLogItem>;

// Background Job types
export interface BackgroundJob {
  job_key: string;
  name: string;
  description: string;
  /** What the job is for (`durable.categories.JobCategory`) and its label. */
  category: string;
  category_label: string;
  /** The one country whose data it syncs (alpha-2, `EU`), else null. */
  country: string | null;
  enabled: boolean;
}

// Training data exports
export interface TrainingDataExport {
  id: string;
  status: "pending" | "running" | "completed" | "failed";
  prompt_slug: string;
  prompt_hash: string;
  filters: Record<string, unknown>;
  s3_prefix: string | null;
  row_count: number | null;
  train_count: number | null;
  val_count: number | null;
  error: string | null;
  progress_processed: number | null;
  progress_written: number | null;
  progress_skipped: number | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

export interface TrainingDataExportFilters {
  min_generated_at?: string;
  max_generated_at?: string;
  countries?: string[];
  max_rows?: number;
}

export interface TrainingDataExportCreate {
  filters: TrainingDataExportFilters;
}

// System Prompt types
export interface SystemPrompt {
  slug: string;
  name: string;
  description: string;
  text: string;
  is_override: boolean;
}

// MFA types
export interface MfaSetupResponse {
  secret: string;
  provisioning_uri: string;
}

export interface MfaVerifyResponse {
  backup_codes: string[];
}

// Auth types
export interface UserOrgMembership {
  id: string;
  name: string;
  roles: string[];
  scopes: string[];
  /** Feature flags the org has switched on (see `auth/features.ts`). */
  features: string[];
}

export interface AuthUser {
  id: string;
  email: string;
  display_name: string;
  roles: string[];
  scopes: string[];
  // The admin view a developer gets with developer mode off; null for non-developers.
  scopes_without_developer: string[] | null;
  is_active: boolean;
  auth_provider: string;
  // Their uploaded photo, else the Google one.
  picture_url: string | null;
  has_uploaded_picture: boolean;
  // False for Google-only accounts: there is no password to change.
  has_password: boolean;
  password_changed_at: string | null;
  mfa_enabled: boolean;
  // Unused backup codes; null while MFA is off.
  mfa_backup_codes_remaining: number | null;
  organizations: UserOrgMembership[];
  last_seen: string | null;
  // Per-user UI settings kept across devices, keyed by feature. See useUiPreference.
  ui_preferences: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

/** One of your signed-in browsers. `id` is a handle, not the session token. */
export interface AccountSession {
  id: string;
  current: boolean;
  created_at: string;
  last_active_at: string;
  expires_at: string;
  ip_address: string | null;
  user_agent: string | null;
}

/** An app (MCP connector) holding OAuth tokens for you. */
export interface ConnectedApp {
  client_id: string;
  name: string;
  scopes: { id: string; label: string }[];
  authorized_at: string;
  last_used_at: string;
}

export interface LoginResponse {
  mfa_required: boolean;
  user: AuthUser | null;
}

export interface Invitation {
  id: string;
  email: string;
  token: string;
  roles: string[];
  organization_id: string | null;
  org_roles: string[];
  invited_by: string;
  accepted_at: string | null;
  expires_at: string;
  created_at: string;
}

// Organization types
export interface Organization {
  id: string;
  name: string;
  google_sso_enabled: boolean;
  google_workspace_domain: string | null;
  google_default_role: string;
  features: string[];
  tenant_count: number;
  created_at: string;
  updated_at: string;
}

/** One entry of the per-organization feature-flag catalog. */
export interface FeatureFlagDefinition {
  key: string;
  label: string;
  description: string;
  default_for_new_orgs: boolean;
}

export interface OrganizationUpdate {
  name?: string;
  google_sso_enabled?: boolean;
  google_workspace_domain?: string | null;
  google_default_role?: string;
}

export interface OrgMember {
  id: string;
  user_id: string;
  organization_id: string;
  roles: string[];
  user_email: string;
  user_display_name: string;
  user_picture_url: string | null;
  user_last_seen: string | null;
  created_at: string;
  groups?: { id: string; name: string }[];
}

export type OrgInviteResult =
  | { status: "added"; member: OrgMember; invitation: null }
  | { status: "invited"; member: null; invitation: Invitation };

export interface OrgRef {
  id: string;
  name: string;
}

export interface OrgTenant {
  id: string;
  organization_id: string;
  tenant_id: string;
  tenant_name: string;
  created_at: string;
  /** Whether this organization owns the tenant. */
  is_primary: boolean;
  primary_organization: OrgRef | null;
  /** The tenant's other linked organizations. */
  shared_with: OrgRef[];
}

// Merge suggestions types
export interface MergeSuggestionCompanySummary {
  id: string;
  name: string;
  country: string;
  domain: string | null;
  org_number: string | null;
  linkedin_url: string | null;
  employee_count: number | null;
}

export interface MergeSuggestion {
  id: string;
  suggestion_type: "merge" | "unmerge";
  source_company: MergeSuggestionCompanySummary | null;
  target_company: MergeSuggestionCompanySummary | null;
  similarity_score: number;
  match_method: string;
  status: string;
  context: Record<string, unknown> | null;
  reviewed_at: string | null;
  created_at: string;
}

export type MergeSuggestionListResponse = Paginated<MergeSuggestion>;

export interface MergeSuggestionStats {
  merge_pending: number;
  merge_approved: number;
  merge_rejected: number;
  unmerge_pending: number;
  unmerge_approved: number;
  unmerge_rejected: number;
}

// --- Inference Instances ---

export interface InferenceInstance {
  id: string;
  hostname: string;
  public_ip: string | null;
  api_url: string;
  api_key: string | null;
  gpu_name: string | null;
  gpu_count: number | null;
  vram_gb: number | null;
  model_name: string | null;
  vllm_version: string | null;
  /** chat = OpenAI chat completions; decision = a SystemOne decision model (signal screening). */
  kind: InferenceInstanceKind;
  status: "pending" | "approved" | "rejected";
  rejection_reason: string | null;
  weight: number;
  /** Served only to bulk callers (signal evaluation, company classification). */
  bulk_only: boolean;
  /** Never served to bulk callers: agents, confirms and one-off calls only. */
  interactive_only: boolean;
  /** Requests/min the instance itself says it takes; null = no limit, routed by weight. */
  max_requests_per_minute: number | null;
  last_heartbeat_at: string | null;
  requests_running: number | null;
  requests_waiting: number | null;
  kv_cache_usage_pct: number | null;
  requests_last_24h: number;
  tokens_prompt_total: number;
  tokens_generation_total: number;
  /** Last 24h in 15-minute buckets; t is the bucket start in epoch seconds. */
  token_buckets: { t: number; p: number; g: number }[] | null;
  /** Prompt + generation tokens/s over 24h; null until tracking starts. */
  tokens_per_sec_24h: number | null;
  extra_info: Record<string, unknown> | null;
  /** The rented box it runs on (inference rentals), if any. */
  rental_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface InferenceInstallLink {
  url: string;
  valid_for_seconds: number;
}

export type InferenceRentalStatus =
  | "creating"
  | "booting"
  | "ready"
  | "destroying"
  | "destroyed"
  | "failed";

/** A GPU box rented from VAST.ai; its servers register as decision instances. */
export interface InferenceRental {
  id: string;
  provider: string;
  machine: string;
  status: InferenceRentalStatus;
  offer_id: number;
  /** The offer as it was when rented. */
  offer: Partial<InferenceRentalOffer>;
  external_id: number | null;
  /** Price per hour in USD, disk included. */
  dph_total: number | null;
  public_ip: string | null;
  ports: Record<string, number> | null;
  error: string | null;
  status_message: string | null;
  created_by_user_id: string | null;
  ready_at: string | null;
  destroyed_at: string | null;
  last_checked_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface InferenceRentalOffer {
  id: number;
  machine_id: number | null;
  gpu_name: string | null;
  num_gpus: number | null;
  geolocation: string | null;
  dph_total: number | null;
  reliability2: number | null;
  inet_down: number | null;
  inet_up: number | null;
  cuda_max_good: number | null;
  cpu_ram: number | null;
  disk_space: number | null;
  verification: string | null;
}

export interface InferenceRentalCredentials {
  provider: string;
  configured: boolean;
  secret_hint: string;
  updated_at: string | null;
  encryption_configured: boolean;
}

/** GPU hardware class with its own install compose. */
export interface InferenceFleet {
  slug: string;
  label: string;
  description: string;
  /** False = sample compose not yet run on real hardware. */
  validated: boolean;
}
